import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transactionAPI, contractAPI, fieldSettingAPI } from '../services/api';
import {
  ArrowLeft, Save, User, MapPin, DollarSign, FileText,
  Building, Plus, X, File, UserCheck, Upload, CheckCircle, Send
} from 'lucide-react';
import { formatCurrency, formatDateForInput, formatContractSaveErrorMessage, formatEformTombCount } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import { useCemeterySettings } from '../contexts/CemeterySettingsContext';
import {
  // validateContractData,
  getNestedFieldError,
  validatePhone,
  validateResidentNumber,
  validateResidentNumberFormat,
  CONTRACT_REQUIRED_MAUSOLEUM_FIELD_CODES,
} from '../utils/validation';
import AddressSearch from '../components/AddressSearch';
import FileUpload from '../components/FileUpload';
import s3Api from '../services/s3Api';
import NumberInput from '../components/NumberInput';
import TemplateSelectionModal from '../components/TemplateSelectionModal';
import ElectronicContractModal from '../components/ElectronicContractModal';
import cemeteryTemplates from '../config/cemetery-templates.json';
import { FIELD_LABEL_DEFAULTS as FL } from '../utils/fieldLabelDefaults';

// V5 상수 정의
const BURIAL_METHODS_1 = ['입관', '퇴관', '봉안', '평장'];
const BURIAL_METHODS_2 = ['쌍분', '단분', '합장', '봉안단지', '매장단지', '평장묘', '가족평장묘'];
const RELATIONSHIPS = ['본인', '배우자', '자녀', '부모', '형제자매', '조부모', '손자녀', '기타'];
const ENGRAVING_TYPES = ['각자비', '재각자비'];

// 서류 타입 상수
const DOCUMENT_TYPES = {
  DEATH_CERTIFICATE: 'death_certificate',
  BURIAL_CERTIFICATE: 'burial_certificate',
  FAMILY_RELATION_CERTIFICATE: 'family_relation_certificate',
  FAMILY_REGISTER: 'family_register',
  CREMATION_CERTIFICATE: 'cremation_certificate',
  PRIVACY_CONSENT: 'privacy_consent',
  EPITAPH: 'epitaph', // V5: 비문 추가
  OTHER: 'other'
};

const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPES.DEATH_CERTIFICATE]: '사망진단서',
  [DOCUMENT_TYPES.BURIAL_CERTIFICATE]: '매장신고서',
  [DOCUMENT_TYPES.FAMILY_RELATION_CERTIFICATE]: '가족관계증명서',
  [DOCUMENT_TYPES.FAMILY_REGISTER]: '제적등본',
  [DOCUMENT_TYPES.CREMATION_CERTIFICATE]: '화장증명서',
  [DOCUMENT_TYPES.PRIVACY_CONSENT]: '개인정보동의서',
  [DOCUMENT_TYPES.EPITAPH]: '비문', // V5: 비문 추가
  [DOCUMENT_TYPES.OTHER]: '기타'
};

const buildEmptyDocumentsState = () =>
  Object.keys(DOCUMENT_TYPE_LABELS).reduce((acc, typeKey) => {
    acc[typeKey] = [];
    return acc;
  }, {});

const normalizeDocumentSlotToArray = (slot) => {
  if (!slot) return [];
  if (Array.isArray(slot)) {
    return slot.map((f) => ({
      id: f.id,
      url: f.url || '',
      name: f.name || ''
    }));
  }
  if (typeof slot === 'object' && slot.url) {
    return [{ id: slot.id, url: slot.url, name: slot.name || '' }];
  }
  return [];
};

const documentsFromApiList = (docList) => {
  const docs = buildEmptyDocumentsState();
  if (!Array.isArray(docList)) return docs;
  docList.forEach((doc) => {
    if (!doc.file_link) return;
    let type = doc.document_type;
    if (type === 'other' && (doc.file_name || '').trim().startsWith('[비문]')) {
      type = DOCUMENT_TYPES.EPITAPH;
    }
    if (!type || !Object.prototype.hasOwnProperty.call(docs, type)) return;
    let name = doc.file_name || '';
    if (type === DOCUMENT_TYPES.EPITAPH && name.startsWith('[비문]')) {
      name = name.replace(/^\[비문\]\s*/, '').trim();
    }
    docs[type].push({
      id: doc.id,
      url: doc.file_link,
      name
    });
  });
  return docs;
};

const serializeDocumentsForApi = (docs) => {
  const out = {};
  if (!docs || typeof docs !== 'object') return out;
  Object.keys(DOCUMENT_TYPE_LABELS).forEach((k) => {
    const list = normalizeDocumentSlotToArray(docs[k]);
    const cleaned = list
      .filter((f) => f && f.url && String(f.url).trim() !== '')
      .map(({ url, name }) => ({ url, name: name || '' }));
    if (cleaned.length) out[k] = cleaned;
  });
  return out;
};

const getDocumentFilesForUi = (docs, key) => {
  const list = normalizeDocumentSlotToArray(docs?.[key]);
  if (list.length === 0) return [{ url: '', name: '' }];
  return list;
};



const ContractFormV5 = ({ isAdditionalBurial = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const { id } = useParams();
  const isEditMode = !!id && !isAdditionalBurial;
  const highLevelContractId = isAdditionalBurial ? id : null;

  // 묘지 설정 Context 사용
  const { getCemeterySettings } = useCemeterySettings();

  // 권한 확인
  const canEdit = hasPermission(user?.role, isEditMode ? 'CONTRACT_EDIT' : 'CONTRACT_CREATE');

  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    contract_date: new Date().toISOString().split('T')[0],
    type: '신규',
    cancellation_date: '',
    relocation_date: '',
    memo: '',
    custom_field_1: '',
    custom_field_2: '',
    custom_field_3: '',
    custom_field_4: '',
    custom_field_5: '',
    dead: [{
      name: '',
      gender: '',
      burial_method_1: '', // V5: 장례구분1
      burial_method_2: '', // V5: 장례구분2
      // tomb_type moved to mausoleum
      death_date: '',
      death_place: '', // V5: 사망장소
      death_cause: '', // V5: 사망원인
      report_date: '',
      resident_number: '', // V5: 주민등록번호 추가
      address: '',
      road_address: '',
      address_detail: '',
      zip_code: ''
    }],
    mausoleum: {
      member_number: '',
      complex: '',
      area: '',
      row_number: '',
      number: '',
      area_size: '', // size → area_size로 변경
      period: '',
      cemetery_id: user?.cemeteryId || 1,
      etc: '',
      tomb_type: '', // V5: 묘타입 추가
      number_of_tombs: 1 // V5: 묘 개수 추가
    },
    acquaintances: [{
      name: '',
      relationship: '',
      relationship_other: '', // V5: 관계 기타 직접 입력
      phone: '',
      address: '',
      road_address: '', // V5: 도로명주소
      zip_code: '', // V5: 우편번호
      detail_address: '', // V5: 상세주소
      is_primary: true, // V5: 주 연락처 표시
      resident_number: '' // V5: 주민등록번호 추가
    }],
    cost: {
      usage_fee: 0,
      management_fee: 0,
      burial_work_fee: 0,
      installation_fee: 0,
      stone_processing_fee: 0, // V5: 석물가공비
      engraving_fee: 0, // V5: 각자비
      maintenance_fee: 0, // V5: 보수비
      additional_costs: [], // V5: 추가 비용 항목
      total_cost: 0,
      memo: ''
    },
    documents: buildEmptyDocumentsState(),
    stone_objects: [], // V5: 석물 정보 (stones에서 변경)
    engraving: [], // V5: 각자 정보
    maintenance: [] // V5: 보수 정보 (수정 시에만)
  });



  // 필드 설정 조회 (사용자의 담당 묘지)
  const { data: fieldSettings } = useQuery({
    queryKey: ['fieldSettings', user?.cemeteryId],
    queryFn: () => {
      console.log('필드 설정 조회 - 사용자 묘지 ID:', user?.cemeteryId);
      return fieldSettingAPI.getFieldSettingsByCemetery(user?.cemeteryId);
    },
    select: (response) => {
      console.log('필드 설정 조회 결과:', response.data);
      return response.data.data; // { success: true, data: {...} }에서 실제 data 추출
    },
    enabled: !!user?.cemeteryId
  });

  // 석물 비용 조회 (사용자의 담당 묘지)
  const { data: stoneCosts } = useQuery({
    queryKey: ['stoneCosts', user?.cemeteryId],
    queryFn: () => {
      console.log('석물 비용 조회 - 사용자 묘지 ID:', user?.cemeteryId);
      return transactionAPI.getStoneCosts(user?.cemeteryId);
    },
    select: (response) => {
      console.log('석물 비용 조회 결과:', response.data?.data);
      return response.data?.data || [];
    },
    enabled: !!user?.cemeteryId
  });

  // 필드 설정 헬퍼 함수
  const getFieldSetting = (fieldCode) => {
    if (!fieldSettings || !fieldSettings.fields) return null;
    return fieldSettings.fields.find(f => f.field_code === fieldCode);
  };

  const isFieldVisible = (fieldCode) => {
    const setting = getFieldSetting(fieldCode);
    if (!setting) return true; // 설정이 없으면 기본적으로 표시
    return setting.is_enabled && setting.is_visible_in_form;
  };

  const isFieldEnabled = (fieldCode) => {
    const setting = getFieldSetting(fieldCode);
    return setting ? setting.is_enabled : true; // 설정이 없으면 기본적으로 활성화
  };

  const getFieldLabel = (fieldCode, defaultLabel) => {
    const setting = getFieldSetting(fieldCode);
    return setting?.custom_label || setting?.label || defaultLabel;
  };

  const isFieldRequired = (fieldCode) => {
    if (CONTRACT_REQUIRED_MAUSOLEUM_FIELD_CODES.includes(fieldCode)) return true;
    const setting = getFieldSetting(fieldCode);
    return setting ? setting.is_required : false;
  };

  const [activeTab, setActiveTab] = useState(isAdditionalBurial ? 'deceased' : 'basic');
  const [stoneCost, setStoneCost] = useState(0);

  const getAcquaintanceRelationshipDisplay = (acquaintance) => {
    if (!acquaintance) return '';

    const relationship = (acquaintance.relationship || '').trim();
    const relationshipOther = (
      acquaintance.relationship_other ||
      acquaintance.relationship_custom ||
      ''
    ).trim();

    if (relationship === '기타') {
      return relationshipOther || '기타';
    }

    return relationship;
  };

  const toFormAcquaintance = (acquaintance = {}, defaultIsPrimary = false) => {
    const relationship = (acquaintance.relationship || '').trim();
    const relationshipOther = (
      acquaintance.relationship_custom ||
      acquaintance.relationship_other ||
      ''
    ).trim();
    const isKnownRelationship = RELATIONSHIPS.includes(relationship);
    const normalizedRelationship = relationship === '기타' || (!isKnownRelationship && relationship)
      ? '기타'
      : relationship;
    const normalizedRelationshipOther = normalizedRelationship === '기타'
      ? (relationshipOther || (!isKnownRelationship ? relationship : ''))
      : '';

    return {
      name: acquaintance.name || '',
      relationship: normalizedRelationship,
      relationship_other: normalizedRelationshipOther,
      phone: acquaintance.phone || '',
      // DB·API의 지번/도로명 모두 유지 (forceEmptyRoadAddress 제거: 저장 시 도로명 누락 방지)
      address: acquaintance.address || '',
      road_address: acquaintance.road_address || '',
      zip_code: acquaintance.zip_code || '',
      detail_address: acquaintance.address_detail || acquaintance.detail_address || '',
      is_primary: acquaintance.is_primary_contact ?? acquaintance.is_primary ?? defaultIsPrimary,
      resident_number: acquaintance.resident_number || ''
    };
  };

  // 기존 계약 데이터 조회 (수정 모드)
  const { data: existingContract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => transactionAPI.getById(id),
    select: (response) => response.data,
    enabled: isEditMode && !!id
  });

  // 안치자 추가 계약 모드일 때 기존 계약 정보 조회
  const { data: parentContract, isLoading: isLoadingParentContract } = useQuery({
    queryKey: ['contract', highLevelContractId],
    queryFn: () => transactionAPI.getById(highLevelContractId),
    select: (response) => response.data,
    enabled: isAdditionalBurial && !!highLevelContractId
  });

  // '예정지' 계약 여부 확인
  const isParentReservedPlot = parentContract?.type === '예정지';

  // 계약 생성/수정 뮤테이션
  const contractMutation = useMutation({
    mutationFn: (data) => {
      if (isAdditionalBurial) {
        // '예정지' 계약인 경우: 기존 계약 업데이트
        if (isParentReservedPlot) {
          console.log('예정지 계약 업데이트:', highLevelContractId, data);
          return transactionAPI.update(highLevelContractId, data);
        }
        // 일반 안치자 추가: 새 하위 계약 생성
        console.log('안치자 추가 계약 생성:', data);
        return transactionAPI.createAdditionalBurial({
          ...data,
          high_level_contract_id: highLevelContractId
        });
      } else if (isEditMode) {
        return transactionAPI.update(id, data);
      } else {
        return transactionAPI.create(data);
      }
    },
    onSuccess: (response) => {
      const message = isEditMode ? '계약이 수정되었습니다.' : '계약이 생성되었습니다.';
      success(message);
      queryClient.invalidateQueries(['contracts']);
      navigate('/contracts');
    },
    onError: (error) => {
      const fallbackMessage = isEditMode ? '계약 수정에 실패했습니다.' : '계약 생성에 실패했습니다.';
      showError(formatContractSaveErrorMessage(error, fallbackMessage));
      console.error('계약 저장 실패:', error);
    }
  });

  // 전자 계약서 발송 뮤테이션
  const eFormMutation = useMutation({
    mutationFn: (data) => contractAPI.sendEForm(data),
    onSuccess: (response) => {
      success('전자 계약서가 발송되었습니다.');
    },
    onError: (error) => {
      showError('전자 계약서 발송에 실패했습니다.');
      console.error('전자 계약서 발송 실패:', error);
    }
  });

  // 기존 데이터 로드
  useEffect(() => {
    if (existingContract && isEditMode) {
      console.log('기존 계약 데이터 로드:', existingContract);
      console.log('계약일:', existingContract.contract_date);
      console.log('계약 유형:', existingContract.type);
      console.log('사망자 정보:', existingContract.deads);
      console.log('묘소 정보:', existingContract.mausoleum);
      console.log('비용 정보:', existingContract.cost);
      console.log('석물 정보:', existingContract.stone_objects);

      setFormData({
        // 기본 계약 정보
        contract_date: formatDateForInput(existingContract.contract_date) || new Date().toISOString().split('T')[0],
        type: existingContract.type || '신규',
        cancellation_date: formatDateForInput(existingContract.contractCancellation?.cancellation_date) || '',
        relocation_date: formatDateForInput(existingContract.contractRelocation?.relocation_date) || '',
        memo: existingContract.memo || '',
        cemetery_id: existingContract.cemetery_id || user?.cemeteryId || 1,
        // 커스텀 필드
        custom_field_1: existingContract.custom_field_1 || '',
        custom_field_2: existingContract.custom_field_2 || '',
        custom_field_3: existingContract.custom_field_3 || '',
        custom_field_4: existingContract.custom_field_4 || '',
        custom_field_5: existingContract.custom_field_5 || '',
        // 묘소 정보
        mausoleum: {
          member_number: existingContract.mausoleum?.member_number || '',
          complex: existingContract.mausoleum?.complex || '',
          area: existingContract.mausoleum?.area || '',
          row_number: existingContract.mausoleum?.row_number || '',
          number: existingContract.mausoleum?.number || '',
          area_size: existingContract.mausoleum?.area_size || '', // size → area_size로 변경
          period: existingContract.mausoleum?.period || '',
          cemetery_id: existingContract.mausoleum?.cemetery_id || user?.cemeteryId || 1,
          etc: existingContract.mausoleum?.etc || '',
          tomb_type: existingContract.mausoleum?.tomb_type || '',
          number_of_tombs: existingContract.mausoleum?.number_of_tombs || 1
        },
        // 사망자 정보 - 백엔드는 'deads', 프론트엔드는 'dead'
        dead: existingContract.deads?.map(dead => ({
          name: dead.name || '',
          gender: dead.gender || '',
          burial_method_1: dead.burial_method_1 || dead.burial_method || '',
          burial_method_2: dead.burial_method_2 || '',
          // tomb_type moved to mausoleum
          death_date: formatDateForInput(dead.death_date) || '',
          death_place: dead.death_place || '',
          death_cause: dead.death_cause || '',
          report_date: formatDateForInput(dead.report_date || dead.burial_date) || '',
          resident_number: dead.resident_number || '',
          address: dead.address || '',
          road_address: dead.road_address || '',
          address_detail: dead.address_detail || '',
          zip_code: dead.zip_code || ''
        })) || [{
          name: '',
          gender: '',
          burial_method_1: '',
          burial_method_2: '',
          tomb_type: '',
          death_date: '',
          death_place: '',
          death_cause: '',
          report_date: '',
          resident_number: '',
          address: '',
          road_address: '',
          address_detail: '',
          zip_code: ''
        }],
        // 연고자 정보: dead의 acquaintance1/2 우선, 없으면 transaction.acquaintances (예정지 등·도로명 유지)
        acquaintances: (() => {
          const accs = [];
          const dead0 = existingContract.deads?.[0];

          if (dead0?.acquaintance1) {
            accs.push(toFormAcquaintance(dead0.acquaintance1, true));
          }
          if (dead0?.acquaintance2) {
            accs.push(toFormAcquaintance(dead0.acquaintance2, false));
          }

          if (accs.length === 0 && existingContract.acquaintances?.length) {
            existingContract.acquaintances.forEach((acc, idx) => {
              accs.push(toFormAcquaintance(acc, idx === 0));
            });
          }

          if (accs.length === 0) {
            accs.push({
              name: '',
              relationship: '',
              relationship_other: '',
              phone: '',
              address: '',
              road_address: '',
              zip_code: '',
              detail_address: '',
              is_primary: true,
              resident_number: ''
            });
          }

          return accs;
        })(),
        // 비용 정보
        cost: {
          usage_fee: existingContract.cost?.usage_fee || 0,
          management_fee: existingContract.cost?.management_fee || 0,
          burial_work_fee: existingContract.cost?.burial_work_fee || 0,
          installation_fee: existingContract.cost?.installation_fee || 0,
          stone_processing_fee: existingContract.cost?.stone_processing_fee || 0,
          engraving_fee: existingContract.cost?.engraving_fee || 0,
          maintenance_fee: existingContract.cost?.maintenance_fee || 0,
          additional_costs: existingContract.cost?.additional_costs || [],
          total_cost: existingContract.cost?.total_cost || 0,
          memo: existingContract.cost?.memo || ''
        },
        // 각자비 정보 - 백엔드는 'engraving'
        engraving: existingContract.engraving?.map(eng => ({
          id: eng.id,
          type: eng.engraving_type || '',
          content: eng.content || '',
          content_file: eng.content_file || null,
          content_file_name: eng.content_file_name || null,
          amount: eng.total_cost || 0,
          status: eng.status || '',
          date: eng.engraving_date || '',
          memo: eng.memo || ''
        })) || [],
        documents: documentsFromApiList(existingContract.documents),
        // 석물 정보 - 백엔드는 'stone_objects'
        stone_objects: existingContract.stone_objects?.map(stone => ({
          id: stone.id,
          stone_name: stone.stone_name || '',
          cost: stone.cost || 0,
          fee_status: stone.fee_status || '미납',
          payment_method: stone.payment_method || '',
          payment_date: stone.payment_date || '',
          memo: stone.memo || ''
        })) || [],
        // 보수 정보 - 백엔드는 'maintenance'
        maintenance: existingContract.maintenance?.map(maint => ({
          id: maint.id,
          type: maint.maintenance_type || '',
          content: maint.content || '',
          amount: maint.cost || 0,
          date: maint.maintenance_date || '',
          status: maint.status || '',
          memo: maint.memo || ''
        })) || []
      });

      console.log('폼 데이터 설정 완료:', formData);
      console.log('연고자 정보:', formData.acquaintances);
      console.log('석물 정보:', formData.stone_objects);
      console.log('각자 정보:', formData.engraving);
      console.log('=== 디버깅 정보 ===');
      console.log('existingContract:', existingContract);
      console.log('existingContract.deads:', existingContract.deads);
      console.log('existingContract.deads[0]:', existingContract.deads?.[0]);
      console.log('existingContract.deads[0].acquaintance1:', existingContract.deads?.[0]?.acquaintance1);
      console.log('existingContract.stone_objects:', existingContract.stone_objects);
      console.log('existingContract.engraving:', existingContract.engraving);
    }
  }, [existingContract, isEditMode]);

  // 안치자 추가 계약 모드일 때 기존 계약 정보 로드
  useEffect(() => {
    if (parentContract && isAdditionalBurial) {
      console.log('안치자 추가 계약 - 기존 계약 데이터 로드:', parentContract);
      console.log('상위 계약 type:', parentContract.type);
      console.log('isParentReservedPlot:', isParentReservedPlot);

      // '예정지' 계약인 경우: 기존 계약 전체를 수정 모드로 로드
      if (isParentReservedPlot) {
        console.log('예정지 계약 → 정식 계약 전환 모드');

        setFormData({
          // 기본 계약 정보 (수정 가능)
          contract_date: formatDateForInput(parentContract.contract_date) || new Date().toISOString().split('T')[0],
          type: '신규', // '예정지' → '신규'로 자동 변경
          cancellation_date: '',
          relocation_date: '',
          memo: parentContract.memo || '',
          cemetery_id: parentContract.cemetery_id || user?.cemeteryId || 1,
          // 커스텀 필드
          custom_field_1: parentContract.custom_field_1 || '',
          custom_field_2: parentContract.custom_field_2 || '',
          custom_field_3: parentContract.custom_field_3 || '',
          custom_field_4: parentContract.custom_field_4 || '',
          custom_field_5: parentContract.custom_field_5 || '',

          // 묘소 정보 (수정 가능)
          mausoleum: {
            member_number: parentContract.mausoleum?.member_number || '',
            complex: parentContract.mausoleum?.complex || '',
            area: parentContract.mausoleum?.area || '',
            row_number: parentContract.mausoleum?.row_number || '',
            number: parentContract.mausoleum?.number || '',
            area_size: parentContract.mausoleum?.area_size || '',
            period: parentContract.mausoleum?.period || '',
            cemetery_id: parentContract.mausoleum?.cemetery_id || user?.cemeteryId || 1,
            etc: parentContract.mausoleum?.etc || '',
            tomb_type: parentContract.mausoleum?.tomb_type || '',
            number_of_tombs: parentContract.mausoleum?.number_of_tombs || 1
          },

          // 기존 사망자 + 새 사망자 추가
          dead: [
            ...(parentContract.deads?.map(dead => ({
              name: dead.name || '',
              gender: dead.gender || '',
              burial_method_1: dead.burial_method_1 || dead.burial_method || '',
              burial_method_2: dead.burial_method_2 || '',
              death_date: formatDateForInput(dead.death_date) || '',
              death_place: dead.death_place || '',
              death_cause: dead.death_cause || '',
              report_date: formatDateForInput(dead.report_date || dead.burial_date) || '',
              resident_number: dead.resident_number || '',
              address: dead.address || '',
              road_address: dead.road_address || '',
              address_detail: dead.address_detail || '',
              zip_code: dead.zip_code || ''
            })) || []),
            // 새 사망자 입력 폼
            {
              name: '',
              gender: '',
              burial_method_1: '',
              burial_method_2: '',
              death_date: '',
              death_place: '',
              death_cause: '',
              report_date: '',
              resident_number: '',
              address: '',
              road_address: '',
              address_detail: '',
              zip_code: ''
            }
          ],

          // 연고자 정보 (기존 연고자)
          acquaintances: (() => {
            const accs = [];

            // 1. parentContract.acquaintances에서 직접 조회 (예정지 계약 등)
            if (parentContract.acquaintances && Array.isArray(parentContract.acquaintances) && parentContract.acquaintances.length > 0) {
              parentContract.acquaintances.forEach(acc => {
                accs.push(toFormAcquaintance(acc));
              });
            }
            // 2. deads[0].acquaintance1/2에서 조회 (일반 계약)
            else {
              if (parentContract.deads?.[0]?.acquaintance1) {
                accs.push(toFormAcquaintance(parentContract.deads[0].acquaintance1, true));
              }
              if (parentContract.deads?.[0]?.acquaintance2) {
                accs.push(toFormAcquaintance(parentContract.deads[0].acquaintance2, false));
              }
            }

            // 연고자가 없으면 빈 폼 추가
            if (accs.length === 0) {
              accs.push({
                name: '',
                relationship: '',
                relationship_other: '',
                phone: '',
                address: '',
                road_address: '',
                zip_code: '',
                detail_address: '',
                is_primary: true,
                resident_number: ''
              });
            }
            return accs;
          })(),

          // 비용 정보
          cost: {
            usage_fee: parentContract.cost?.usage_fee || 0,
            management_fee: parentContract.cost?.management_fee || 0,
            burial_work_fee: parentContract.cost?.burial_work_fee || 0,
            installation_fee: parentContract.cost?.installation_fee || 0,
            stone_processing_fee: parentContract.cost?.stone_processing_fee || 0,
            engraving_fee: parentContract.cost?.engraving_fee || 0,
            maintenance_fee: parentContract.cost?.maintenance_fee || 0,
            additional_costs: parentContract.cost?.additional_costs || [],
            total_cost: parentContract.cost?.total_cost || 0,
            memo: parentContract.cost?.memo || ''
          },

          // 각자비 정보
          engraving: parentContract.engraving?.map(eng => ({
            id: eng.id,
            type: eng.engraving_type || '',
            content: eng.content || '',
            cost: eng.total_cost || 0,
            status: eng.status || 'pending',
            date: formatDateForInput(eng.engraving_date) || '',
            memo: eng.memo || ''
          })) || [],

          // 석물 정보
          stone_objects: parentContract.stone_objects?.map(stone => ({
            id: stone.id,
            name: stone.stone_name || '',
            cost: stone.cost || 0,
            status: stone.fee_status || '미납',
            payment_method: stone.payment_method || '',
            payment_date: formatDateForInput(stone.payment_date) || ''
          })) || [],

          documents: documentsFromApiList(parentContract.documents),

          maintenance: []
        });

        return; // '예정지' 처리 완료, 아래 일반 안치자 추가 로직 실행 안 함
      }

      // 일반 안치자 추가: 기존 로직 유지
      // 기존 계약 정보를 formData에 설정 (참조용, 수정 불가)
      const normalizeRn = (rn) => rn ? rn.replace(/[^0-9]/g, '') : '';
      const normalizePhone = (p) => p ? p.replace(/[^0-9]/g, '') : '';
      const makeKey = (acc) => {
        const rn = normalizeRn(acc?.resident_number);
        if (rn) return `rn:${rn}`;
        const name = (acc?.name || '').trim();
        const phone = normalizePhone(acc?.phone);
        if (name && phone) return `np:${name}|${phone}`;
        if (name) return `n:${name}`;
        return null;
      };

      const parentAcquaintances = [];
      if (Array.isArray(parentContract.deads)) {
        parentContract.deads.forEach((d) => {
          if (d?.acquaintance1) parentAcquaintances.push(d.acquaintance1);
          if (d?.acquaintance2) parentAcquaintances.push(d.acquaintance2);
        });
      }

      // 중복 제거
      const uniqueAccMap = new Map();
      parentAcquaintances.forEach((acc) => {
        const key = makeKey(acc) || `idx:${uniqueAccMap.size}`;
        if (!uniqueAccMap.has(key)) {
          uniqueAccMap.set(key, toFormAcquaintance(acc));
        }
      });

      setFormData(prev => ({
        ...prev,
        // 기존 계약의 묘소 정보, 연고자 정보 등은 참조만 (표시용)
        mausoleum: {
          member_number: parentContract.mausoleum?.member_number || '',
          complex: parentContract.mausoleum?.complex || '',
          area: parentContract.mausoleum?.area || '',
          row_number: parentContract.mausoleum?.row_number || '',
          number: parentContract.mausoleum?.number || '',
          area_size: parentContract.mausoleum?.area_size || '',
          period: parentContract.mausoleum?.period || '',
          cemetery_id: parentContract.mausoleum?.cemetery_id || user?.cemeteryId || 1,
          etc: parentContract.mausoleum?.etc || '',
          tomb_type: parentContract.mausoleum?.tomb_type || '',
          number_of_tombs: parentContract.mausoleum?.number_of_tombs || 1
        },
        // 연고자 정보 (부모 계약 전체 연고자를 표시/편집)
        acquaintances: Array.from(uniqueAccMap.values()),
        // 새로운 정보는 빈 상태로 유지 (사용자가 입력)
        dead: [{
          name: '',
          gender: '',
          burial_method_1: '',
          burial_method_2: '',
          death_date: '',
          death_place: '',
          death_cause: '',
          report_date: '',
          resident_number: '',
          address: '',
          road_address: '',
          address_detail: '',
          zip_code: ''
        }],
        // 비용 정보: 안치자 추가 회차별 금액만 입력 (저장 시 부모 계약에 "사용료 n" 등으로 반영)
        cost: {
          ...prev.cost,
          usage_fee: 0,
          burial_work_fee: 0,
          installation_fee: 0
        },
        // 석물, 각자, 서류는 빈 상태로 시작
        stone_objects: [],
        engraving: [],
        documents: buildEmptyDocumentsState()
      }));
    }
  }, [parentContract, isAdditionalBurial, user?.cemeteryId]);

  // 전화번호 포맷팅 함수 (XXX-XXXX-XXXX)
  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '');

    // 최대 11자리까지만 허용
    const limitedNumbers = numbers.slice(0, 11);

    // 포맷팅: XXX-XXXX-XXXX
    if (limitedNumbers.length <= 3) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
    } else {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7)}`;
    }
  };

  // 주민등록번호 포맷팅 함수 (XXXXXX-XXXXXXX)
  const formatResidentNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '');

    // 최대 13자리까지만 허용
    const limitedNumbers = numbers.slice(0, 13);

    // 포맷팅: XXXXXX-XXXXXXX
    if (limitedNumbers.length <= 6) {
      return limitedNumbers;
    } else {
      return `${limitedNumbers.slice(0, 6)}-${limitedNumbers.slice(6)}`;
    }
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (section, field, value, index = 0) => {
    setFormData(prev => {
      const newData = { ...prev };

      if (section === 'main') {
        newData[field] = value;
      } else if (section === 'dead') {
        // 주민등록번호 필드인 경우 자동 포맷팅 적용
        if (field === 'resident_number') {
          const formattedValue = formatResidentNumber(value);
          newData.dead[index] = { ...newData.dead[index], [field]: formattedValue };

          // 실시간 검증 (형식만 체크)
          if (formattedValue && formattedValue.length > 0) {
            if (!validateResidentNumberFormat(formattedValue)) {
              setValidationErrors(prev => ({
                ...prev,
                [`dead.${index}.resident_number`]: '올바른 주민등록번호 형식이 아닙니다. (예: 000000-0000000)'
              }));
            } else {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`dead.${index}.resident_number`];
                return newErrors;
              });
            }
          } else {
            // 빈 값이면 에러 제거
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`dead.${index}.resident_number`];
              return newErrors;
            });
          }
        } else {
          newData.dead[index] = { ...newData.dead[index], [field]: value };
        }
      } else if (section === 'mausoleum') {
        newData.mausoleum = { ...newData.mausoleum, [field]: value };
      } else if (section === 'acquaintance') {
        // 연락처 필드인 경우 자동 포맷팅 적용
        if (field === 'phone') {
          const formattedValue = formatPhoneNumber(value);
          newData.acquaintances[index] = { ...newData.acquaintances[index], [field]: formattedValue };

          // 실시간 검증
          if (formattedValue && formattedValue.length > 0) {
            if (!validatePhone(formattedValue)) {
              setValidationErrors(prev => ({
                ...prev,
                [`acquaintances.${index}.phone`]: '올바른 휴대폰 번호 형식이 아닙니다. (예: 010-1234-5678)'
              }));
            } else {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`acquaintances.${index}.phone`];
                return newErrors;
              });
            }
          } else {
            // 빈 값이면 에러 제거
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`acquaintances.${index}.phone`];
              return newErrors;
            });
          }
        } else if (field === 'resident_number') {
          // 주민등록번호 필드인 경우 자동 포맷팅 적용
          const formattedValue = formatResidentNumber(value);
          newData.acquaintances[index] = { ...newData.acquaintances[index], [field]: formattedValue };

          // 실시간 검증 (형식만 체크)
          if (formattedValue && formattedValue.length > 0) {
            if (!validateResidentNumberFormat(formattedValue)) {
              setValidationErrors(prev => ({
                ...prev,
                [`acquaintances.${index}.resident_number`]: '올바른 주민등록번호 형식이 아닙니다. (예: 000000-0000000)'
              }));
            } else {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`acquaintances.${index}.resident_number`];
                return newErrors;
              });
            }
          } else {
            // 빈 값이면 에러 제거
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`acquaintances.${index}.resident_number`];
              return newErrors;
            });
          }
        } else {
          newData.acquaintances[index] = { ...newData.acquaintances[index], [field]: value };
        }
      } else if (section === 'cost') {
        newData.cost = { ...newData.cost, [field]: value };
      }

      return newData;
    });
  };

  // 주소 선택 핸들러 (연고자용)
  const handleAddressSelect = (address, index = 0, type = 'acquaintance') => {
    console.log('주소 선택됨:', address, '인덱스:', index, '타입:', type);
    if (address) {
      setFormData(prev => {
        if (type === 'dead') {
          // 사망자 주소 선택
          return {
            ...prev,
            dead: prev.dead.map((d, i) =>
              i === index ? {
                ...d,
                address: address.address,
                road_address: address.roadAddress,
                zip_code: address.zipCode
              } : d
            )
          };
        } else {
          // 연고자 주소 선택 (기존 로직)
          return {
            ...prev,
            acquaintances: prev.acquaintances.map((acc, i) =>
              i === index ? {
                ...acc,
                address: address.address,
                road_address: address.roadAddress,
                zip_code: address.zipCode
              } : acc
            )
          };
        }
      });
    }
  };

  // 각자 정보 파일 업로드 핸들러
  const handleEngravingFileUpload = async (file, index) => {
    if (file) {
      try {
        // 고유한 파일명 생성
        const timestamp = Date.now();
        const fileName = `engravings/${timestamp}_${file.name}`;

        // S3 업로드 URL 생성
        const uploadData = await s3Api.generateUploadUrl(fileName, file.type);

        // 파일 업로드
        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (uploadResponse.ok) {
          const fileUrl = convertPresignedUrlToPublicUrl(uploadData.uploadUrl).fileUrl;

          const newEngraving = [...(formData.engraving || [])];
          newEngraving[index].content_file = fileUrl;
          newEngraving[index].content_file_name = file.name;
          newEngraving[index].content = `파일: ${file.name}`;
          setFormData(prev => ({ ...prev, engraving: newEngraving }));
        } else {
          throw new Error('파일 업로드에 실패했습니다.');
        }
      } catch (error) {
        console.error('각자정보 파일 업로드 오류:', error);
        alert('파일 업로드 중 오류가 발생했습니다.');
      }
    }
  };

  // 각자 정보 파일 제거 핸들러
  const handleEngravingFileRemove = (index) => {
    const newEngraving = [...(formData.engraving || [])];
    newEngraving[index].content_file = null;
    newEngraving[index].content_file_name = null;
    newEngraving[index].content = '';
    setFormData(prev => ({ ...prev, engraving: newEngraving }));
  };

  // S3 presigned URL을 공개 URL로 변환
  const convertPresignedUrlToPublicUrl = (presignedUrl) => {
    if (typeof presignedUrl !== 'string' || !presignedUrl.startsWith('http')) {
      console.error('Invalid presigned URL:', presignedUrl);
      return null;
    }

    try {
      const url = new URL(presignedUrl.trim());
      const hostParts = url.hostname.split('.');

      if (hostParts.length < 5) {
        console.error('Unexpected hostname format:', url.hostname);
        return null;
      }

      const bucketName = hostParts[0];
      const region = hostParts[2];
      const filePath = url.pathname.replace(/^\/+/, '');

      const fileUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
      return { fileUrl, bucketName, region, filePath };
    } catch (error) {
      console.error('Failed to parse URL:', error);
      return null;
    }
  };

  // 사망자 추가/삭제
  const addDead = () => {
    setFormData(prev => ({
      ...prev,
      dead: [...prev.dead, {
        name: '',
        gender: '',
        burial_method_1: '',
        burial_method_2: '',
        // tomb_type removed from dead
        death_date: '',
        death_place: '',
        death_cause: '',
        report_date: '',
        resident_number: '',
        address: '',
        road_address: '',
        address_detail: '',
        zip_code: ''
      }]
    }));
  };

  const removeDead = (index) => {
    setFormData(prev => ({
      ...prev,
      dead: prev.dead.filter((_, i) => i !== index)
    }));
  };

  // 연고자 추가/삭제
  const addAcquaintance = () => {
    setFormData(prev => ({
      ...prev,
      acquaintances: [...prev.acquaintances, {
        name: '',
        relationship: '',
        relationship_other: '',
        phone: '',
        address: '',
        road_address: '',
        zip_code: '',
        detail_address: '',
        is_primary: false,
        resident_number: ''
      }]
    }));
  };

  const removeAcquaintance = (index) => {
    setFormData(prev => ({
      ...prev,
      acquaintances: prev.acquaintances.filter((_, i) => i !== index)
    }));
  };

  // 추가 비용 항목 관리
  const addCostItem = () => {
    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        additional_costs: [...(prev.cost.additional_costs || []), {
          name: '',
          amount: 0
        }]
      }
    }));
  };

  const removeCostItem = (index) => {
    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        additional_costs: (prev.cost.additional_costs || []).filter((_, i) => i !== index)
      }
    }));
  };

  // 각자 정보 관리
  const addEngraving = () => {
    setFormData(prev => ({
      ...prev,
      engraving: [...prev.engraving, {
        type: '각자비',
        content: '',
        content_file: null, // 임시로 null로 설정 (데이터베이스에 컬럼이 없음)
        amount: 0
      }]
    }));
    // 각자 정보 추가 후 비용 재계산
    setTimeout(() => calculateEngravingCost(), 0);
  };

  const removeEngraving = (index) => {
    setFormData(prev => ({
      ...prev,
      engraving: prev.engraving.filter((_, i) => i !== index)
    }));
    // 각자 정보 삭제 후 비용 재계산
    setTimeout(() => calculateEngravingCost(), 0);
  };

  // 보수 정보 관리 (수정 모드에서만)
  const addMaintenance = () => {
    setFormData(prev => ({
      ...prev,
      maintenance: [...prev.maintenance, {
        content: '',
        amount: 0
      }]
    }));
    // 보수 정보 추가 후 비용 재계산
    setTimeout(() => calculateMaintenanceCost(), 0);
  };

  const removeMaintenance = (index) => {
    setFormData(prev => ({
      ...prev,
      maintenance: prev.maintenance.filter((_, i) => i !== index)
    }));
    // 보수 정보 삭제 후 비용 재계산
    setTimeout(() => calculateMaintenanceCost(), 0);
  };

  // 묘소 크기 변경 시 사용료 자동 계산 (관리비는 후불 발생)
  const calculateFeesFromSize = (size) => {
    if (!size || !user?.cemeteryId) return;

    console.log('=== 묘소 크기 변경 시 비용 계산 ===');
    console.log('묘지 ID:', user?.cemeteryId);
    console.log('묘소 크기:', size);

    const cemeterySettings = getCemeterySettings(user.cemeteryId);
    console.log('묘지 설정:', cemeterySettings);

    const areaSize = parseFloat(size) || 0;
    console.log('면적:', areaSize);

    if (areaSize > 0) {
      // 사용료 계산: 평당 사용료 × 면적
      const usageFee = (parseFloat(cemeterySettings.usage_fee_per_sqm) || 0) * areaSize;
      console.log('평당 사용료:', cemeterySettings.usage_fee_per_sqm);
      console.log('계산된 사용료:', usageFee);

      // 관리비는 후불로 발생하므로 계약 시점에는 0으로 설정
      // 실제 관리비는 계약 기간 이후에 자동으로 발생됨

      setFormData(prev => ({
        ...prev,
        cost: {
          ...prev.cost,
          usage_fee: Math.round(usageFee),
          management_fee: 0 // 후불 발생
        }
      }));

      console.log('비용 업데이트 완료');
    }
  };

  // 석물 정보 변경 시 석물 비용 자동 계산
  const calculateStoneCost = () => {
    const stoneObjects = formData.stone_objects || [];
    const totalStoneCost = stoneObjects.reduce((sum, stone) => {
      return sum + (parseFloat(stone.cost) || 0);
    }, 0);

    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        stone_processing_fee: totalStoneCost
      }
    }));

    // stoneCost 상태도 업데이트 (비용 정보 탭 표시용)
    setStoneCost(totalStoneCost);
  };

  // 각자 정보 변경 시 각자 비용 자동 계산
  const calculateEngravingCost = () => {
    const engravings = formData.engraving || [];
    const totalEngravingCost = engravings.reduce((sum, engraving) => {
      return sum + (parseFloat(engraving.amount) || 0);
    }, 0);

    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        engraving_fee: totalEngravingCost
      }
    }));
  };

  // 보수 정보 변경 시 보수 비용 자동 계산
  const calculateMaintenanceCost = () => {
    const maintenance = formData.maintenance || [];
    const totalMaintenanceCost = maintenance.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);

    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        maintenance_fee: totalMaintenanceCost
      }
    }));
  };

  // 관리비 계산: (묘지 기본 관리비) * (평수) * (계약 기간)
  const getCalculatedManagementFee = () => {
    const cemeterySettings = getCemeterySettings(user?.cemeteryId);
    const managementFeeBase = parseFloat(cemeterySettings?.management_fee_per_sqm) || 0;
    const areaSize = parseFloat(formData.mausoleum?.area_size) || 0;
    const period = parseFloat(formData.mausoleum?.period) || 0;
    return managementFeeBase * areaSize * period;
  };

  // 석물 비용 변경 핸들러
  const handleStoneCostChange = (cost) => {
    setStoneCost(cost);
    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        stone_processing_fee: cost
      }
    }));
  };

  // 총 비용 계산
  const calculateTotalCost = () => {
    const managementPart = isAdditionalBurial ? 0 : getCalculatedManagementFee();
    const baseCosts =
      parseFloat(formData.cost.usage_fee || 0) +
      parseFloat(managementPart) +
      parseFloat(formData.cost.burial_work_fee || 0) +
      parseFloat(formData.cost.installation_fee || 0) +
      parseFloat(formData.cost.stone_processing_fee || 0);

    const additionalCosts = (formData.cost.additional_costs || []).reduce((sum, item) =>
      sum + parseFloat(item.amount || 0), 0
    );

    // const engravingCosts = parseFloat(formData.cost.engraving_fee || 0);
    // const engravingCosts = (formData.engraving || []).reduce((sum, engraving) => {
    //   return sum + (parseFloat(engraving.amount) || 0);
    // }, 0);

    const newEngravingCost = formData.engraving?.reduce((sum, eng) => {
      const amount = parseFloat(eng.amount) || 0;
      console.log('eng:', eng, 'amount:', amount);
      return sum + amount;
    }, 0) || 0;


    const maintenanceCosts = parseFloat(formData.cost.maintenance_fee || 0);

    // return baseCosts + additionalCosts + engravingCosts + newEngravingCost + maintenanceCosts;
    return baseCosts + additionalCosts + newEngravingCost + maintenanceCosts;
  };

  // 묘지별 템플릿 정보
  const cemeteryTemplatesData = cemeteryTemplates.cemeteries.find(c => c.cemetery_id === user?.cemeteryId);
  const availableTemplates = cemeteryTemplatesData?.templates || [];

  // 모달 상태
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showElectronicModal, setShowElectronicModal] = useState(false);

  // 전자 계약서 발송 핸들러
  const handleSendEForm = () => {
    if (!canEdit) {
      showError('전자 계약서를 발송할 권한이 없습니다.');
      return;
    }

    if (!availableTemplates || availableTemplates.length === 0) {
      showError('사용 가능한 계약서 템플릿이 없습니다.');
      return;
    }

    // 템플릿 선택 모달 열기
    setShowTemplateModal(true);
  };

  /**
   * 연고자 등록 순서 규칙
   * - 1순위: `is_primary` 체크 여부가 true인 항목을 먼저(내장된 나열 순서 유지)
   * - 2순위: 나열된 순서(index) 유지
   *
   * 서버는 `acquaintances[0]`을 사망자 acquaintance1_id로,
   * `acquaintances[1]`을 acquaintance2_id로 매핑하므로 이 순서를 반드시 보장해야 합니다.
   */
  const getOrderedAcquaintances = (acquaintances = []) => {
    const withIndex = (acquaintances || []).map((acc, idx) => ({
      acc,
      idx,
    }));

    withIndex.sort((a, b) => {
      const aPrimary =
        a.acc?.is_primary === true ||
        a.acc?.is_primary === 1 ||
        a.acc?.is_primary === 'true' ||
        a.acc?.is_primary_contact === true ||
        a.acc?.is_primary_contact === 1;
      const bPrimary =
        b.acc?.is_primary === true ||
        b.acc?.is_primary === 1 ||
        b.acc?.is_primary === 'true' ||
        b.acc?.is_primary_contact === true ||
        b.acc?.is_primary_contact === 1;
      if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
      return a.idx - b.idx;
    });

    return withIndex.map(({ acc }) => acc);
  };

  // 템플릿 선택 핸들러
  const handleTemplateSelect = (template) => {
    setShowTemplateModal(false);

    // 계약자 정보 추출 (첫 번째 연락처)
    const orderedAcquaintances = getOrderedAcquaintances(formData.acquaintances);
    const primaryContact = orderedAcquaintances[0];

    if (!primaryContact || !primaryContact.name || !primaryContact.phone) {
      showError('계약자 정보(이름, 휴대폰번호)를 확인해주세요.');
      return;
    }

    // 숫자 포맷팅 헬퍼 함수 (천단위 쉼표)
    const formatNumber = (value) => {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('ko-KR');
    };

    // 묘지 설정 가져오기
    const cemeterySettings = getCemeterySettings(user?.cemeteryId);
    const areaSize = parseFloat(formData.mausoleum?.area_size) || 0;

    // 필드 매핑 함수
    const mapFieldValue = (fieldId) => {
      switch (fieldId) {
        case '묘적번호':
          return formData.mausoleum?.member_number || '';
        case '사망자 성명':
          return formData.dead?.[0]?.name || '';
        case '사망자 성별':
          return formData.dead?.[0]?.gender || '';
        case '사망일자':
          return formData.dead?.[0]?.death_date || '';
        case '장례일자':
          return formData.dead?.[0]?.report_date || '';
        case '사망자 주민등록번호':
          return formData.dead?.[0]?.resident_number || '';
        case '단지':
          return formData.mausoleum?.complex || '';
        case '묘역':
          return formData.mausoleum?.area || '';
        case '열':
          return formData.mausoleum?.row_number || '';
        case '호':
          return formData.mausoleum?.number || '';
        case '평형':
          return formData.mausoleum?.area_size || '';
        case '기수':
          return formatEformTombCount(formData.mausoleum);
        case '장례구분1':
          return formData.dead?.[0]?.burial_method_1 || '';
        case '장례구분2':
          return formData.dead?.[0]?.burial_method_2 || '';
        case '관리비 발생 주기':
        case '관리비 발생주기': {
          const period = formData.mausoleum?.period;
          // period가 숫자 문자열이거나 숫자인지 확인
          const periodValue = period ? (typeof period === 'string' ? parseInt(period, 10) : period) : null;
          return periodValue ? periodValue.toString() : '5';
        }
        case '사용료':
          return formatNumber(formData.cost?.usage_fee || 0);
        case '관리비': {
          if (isAdditionalBurial) {
            return '0';
          }
          // (묘지 기본 관리비) * (평수) * (계약 기간)
          const calculatedManagementFee = getCalculatedManagementFee();
          return formatNumber(calculatedManagementFee);
        }
        case '작업비':
          return formatNumber(formData.cost?.burial_work_fee || 0);
        case '총액': {
          // 전자계약서용 총액: 사용료 + 관리비 + 매장작업비 (+ 안치자 추가 시 설치비)
          const usageFee = parseFloat(formData.cost?.usage_fee || 0);
          const managementFee = isAdditionalBurial ? 0 : getCalculatedManagementFee();
          const burialWorkFee = parseFloat(formData.cost?.burial_work_fee || 0);
          const installationFee = parseFloat(formData.cost?.installation_fee || 0);
          return formatNumber(usageFee + managementFee + burialWorkFee + installationFee);
        }
        case '계약 연도':
          return new Date(formData.contract_date).getFullYear().toString();
        case '계약 월':
          return (new Date(formData.contract_date).getMonth() + 1).toString();
        case '계약 일':
          return new Date(formData.contract_date).getDate().toString();
        case '사망자 주소': {
          const roadAddress = formData.dead?.[0]?.road_address || '';
          const addressDetail = formData.dead?.[0]?.address_detail || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자1 주소': {
          const roadAddress = primaryContact?.road_address || '';
          const addressDetail = primaryContact?.detail_address || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자1 성명':
          return primaryContact.name || '';
        case '연고자1 전화번호':
          return primaryContact.phone || '';
        case '연고자1 관계':
          return getAcquaintanceRelationshipDisplay(primaryContact);
        case '연고자1 주민등록번호':
          return primaryContact.resident_number || '';
        case '연고자2 성명':
          return orderedAcquaintances[1]?.name || '';
        case '연고자2 전화번호':
          return orderedAcquaintances[1]?.phone || '';
        case '연고자2 관계':
          return getAcquaintanceRelationshipDisplay(orderedAcquaintances[1]);
        case '연고자2 주소': {
          const roadAddress = orderedAcquaintances[1]?.road_address || '';
          const addressDetail = orderedAcquaintances[1]?.detail_address || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자2 주민등록번호':
          return orderedAcquaintances[1]?.resident_number || '';
        case '석물비용': {
          // stone_objects의 모든 cost 합계
          const totalStoneCost = (formData.stone_objects || []).reduce((sum, stone) => {
            return sum + (parseFloat(stone.cost) || 0);
          }, 0);
          // 각자비(engraving)의 모든 금액 합계 (eng.amount 또는 eng.total_cost)
          const totalEngravingCost = (formData.engraving || []).reduce((sum, eng) => {
            const amount = parseFloat(eng.amount || eng.total_cost || 0);
            return sum + amount;
          }, 0);
          const installationFee = parseFloat(formData.cost?.installation_fee || 0);
          // 석물 총합 + 각자비 총합 + 설치비
          return formatNumber(totalStoneCost + totalEngravingCost + installationFee);
        }
        case '각자비_메모': {
          // 각자비(engraving)의 모든 금액 합계
          const totalEngravingCost = (formData.engraving || []).reduce((sum, eng) => {
            const amount = parseFloat(eng.amount || eng.total_cost || 0);
            return sum + amount;
          }, 0);
          // 각자비가 있는 경우에만 메모 작성
          if (totalEngravingCost > 0) {
            return `각자비 ${formatNumber(totalEngravingCost)}원`;
          }
          return '';
        }
        default:
          // 석물 필드 처리 (석물1~N, 각자비 포함)
          // stone_objects를 stone_name별로 그룹화하여 처리
          const stoneObjects = formData.stone_objects || [];

          // stone_name별로 그룹화 (같은 이름의 석물들을 묶음)
          const groupedStones = stoneObjects.reduce((acc, stone) => {
            const name = stone.stone_name || '';
            if (!acc[name]) {
              acc[name] = {
                stone_name: name,
                count: 0,
                totalCost: 0,
                stones: []
              };
            }
            acc[name].count += 1;
            acc[name].totalCost += parseFloat(stone.cost) || 0;
            acc[name].stones.push(stone);
            return acc;
          }, {});

          // 그룹화된 석물들을 배열로 변환 (정렬: 이름순)
          const groupedStonesArray = Object.values(groupedStones).sort((a, b) =>
            (a.stone_name || '').localeCompare(b.stone_name || '')
          );

          // 각자비(engraving)를 석물 항목에 추가
          // 각자비는 각 항목을 별도로 추가 (품목: 각자 종류, 가격: 금액, 수량: 1)
          const engravingItems = (formData.engraving || [])
            .map((eng, idx) => {
              // 각자 종류: eng.type 또는 eng.engraving_type (빈 문자열 처리)
              const engravingType = (eng.type || eng.engraving_type || '').trim() || `각자비${idx + 1}`;
              // 금액: eng.amount 또는 eng.total_cost
              const engravingAmount = parseFloat(eng.amount || eng.total_cost || 0);
              return {
                stone_name: engravingType,
                count: 1, // 각자비는 항목당 1개
                totalCost: engravingAmount,
                isEngraving: true
              };
            })
            .filter(item => item.totalCost > 0); // 금액이 0보다 큰 항목만 포함

          // 설치비가 있으면 석물비용 항목 마지막에 "설치비"를 추가
          const installationFee = parseFloat(formData.cost?.installation_fee || 0);
          const installationItems = installationFee > 0 ? [{
            stone_name: '설치비',
            count: 1,
            totalCost: installationFee,
            isInstallationFee: true
          }] : [];

          // 석물 + 각자비 + 설치비를 합친 배열 (설치비는 마지막)
          const allItems = [...groupedStonesArray, ...engravingItems, ...installationItems];

          if (fieldId.startsWith('석물') && fieldId.includes('품목')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              return allItems[parseInt(stoneIndex) - 1].stone_name || '';
            }
            return '';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('규격')) {
            // 규격 필드는 현재 stone_objects에 없으므로 빈 문자열 반환
            return '';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('수량')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              // 같은 stone_name의 개수 누산 (각자비는 항상 1)
              return allItems[parseInt(stoneIndex) - 1].count.toString();
            }
            return '0';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('가격')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              // 같은 stone_name의 총 비용
              return formatNumber(allItems[parseInt(stoneIndex) - 1].totalCost);
            }
            return '0';
          }
          return '';
      }
    };

    // 필드 데이터 구성
    // 석물 + 각자비 + 설치비의 총 개수 계산
    const totalStoneAndEngravingCount = (formData.stone_objects || []).length +
      (formData.engraving || []).filter(eng => parseFloat(eng.amount || eng.total_cost || 0) > 0).length +
      ((parseFloat(formData.cost?.installation_fee || 0) > 0) ? 1 : 0);

    console.log('=== ContractFormV5 필드 필터링 ===');
    console.log('stone_objects 개수:', (formData.stone_objects || []).length);
    console.log('engraving 개수:', (formData.engraving || []).filter(eng => parseFloat(eng.amount || eng.total_cost || 0) > 0).length);
    console.log('totalStoneAndEngravingCount:', totalStoneAndEngravingCount);
    console.log('template.field_ids:', template.field_ids);
    console.log('각자비_메모 포함 여부:', template.field_ids.includes('각자비_메모'));

    const fields = template.field_ids
      .filter(fieldId => {
        // '석물비용'은 항상 포함
        if (fieldId === '석물비용') {
          console.log('석물비용 필드 포함');
          return true;
        }
        // '각자비_메모'는 각자비가 있으면 포함
        if (fieldId === '각자비_메모') {
          console.log('각자비_메모 필드 포함 (무조건)');
          return true;
        }
        // Only include stone object fields (석물1~N) if stone objects or engraving exist
        if (fieldId.startsWith('석물')) {
          const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
          if (stoneIndex) {
            const index = parseInt(stoneIndex);
            // 석물 + 각자비 총 개수 이하면 포함
            const shouldInclude = index <= totalStoneAndEngravingCount;
            console.log(`필드 ${fieldId}: index=${index}, totalCount=${totalStoneAndEngravingCount}, 포함여부=${shouldInclude}`);
            return shouldInclude;
          }
          return false;
        }
        // Only include 연고자2 fields if second acquaintance exists
        if (fieldId.includes('연고자2')) {
          return orderedAcquaintances?.[1] ? true : false;
        }
        return true;
      })
      .map(fieldId => {
        const value = mapFieldValue(fieldId);
        console.log(`필드 매핑: ${fieldId} = "${value}"`);
        return {
          id: fieldId,
          value: value
        };
      });

    console.log('=== 최종 fields 배열 ===');
    console.log('총 필드 개수:', fields.length);
    console.log('각자비_메모 필드:', fields.find(f => f.id === '각자비_메모'));

    // API 호출 데이터 구성
    const contractData = {
      contractorName: primaryContact.name,
      contractorPhone: primaryContact.phone,
      cemeteryId: user?.cemeteryId,
      templateId: template.template_id,
      templateName: template.template_name,
      fields: fields
    };

    eFormMutation.mutate(contractData);
  };

  // 폼 제출
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!canEdit) {
      showError('계약을 생성/수정할 권한이 없습니다.');
      return;
    }

    // 주민등록번호 및 휴대폰 번호 검증
    const fieldErrors = {};

    // 사망자 주민등록번호 검증
    formData.dead?.forEach((dead, index) => {
      if (dead.resident_number && dead.resident_number.trim() !== '') {
        if (!validateResidentNumber(dead.resident_number)) {
          fieldErrors[`dead.${index}.resident_number`] = `사망자 ${index + 1}의 올바른 주민등록번호 형식이 아닙니다. (예: 000000-0000000)`;
        }
      }
    });

    // 연고자 휴대폰 번호 및 주민등록번호 검증
    formData.acquaintances?.forEach((acquaintance, index) => {
      if (acquaintance.phone && acquaintance.phone.trim() !== '') {
        if (!validatePhone(acquaintance.phone)) {
          fieldErrors[`acquaintances.${index}.phone`] = `연고자 ${index + 1}의 올바른 휴대폰 번호 형식이 아닙니다. (예: 010-1234-5678)`;
        }
      }
      if (acquaintance.resident_number && acquaintance.resident_number.trim() !== '') {
        if (!validateResidentNumber(acquaintance.resident_number)) {
          fieldErrors[`acquaintances.${index}.resident_number`] = `연고자 ${index + 1}의 올바른 주민등록번호 형식이 아닙니다. (예: 000000-0000000)`;
        }
      }
    });

    // 데이터 검증
    // const validation = validateContractData(formData);

    // 필드 검증 에러와 일반 검증 에러 병합
    // const allErrors = { ...validation.errors, ...fieldErrors };

    // if (!validation.isValid || Object.keys(fieldErrors).length > 0) {
    //   setValidationErrors(allErrors);

    //   // 에러 메시지 포맷팅
    //   const errorMessages = Object.values(allErrors);
    //   const errorText = errorMessages.length > 0
    //     ? `입력 정보를 확인해주세요.\n\n${errorMessages.slice(0, 3).join('\n')}${errorMessages.length > 3 ? `\n... 외 ${errorMessages.length - 3}개 항목` : ''}`
    //     : '입력 정보를 확인해주세요.';

    //   showError(errorText);
    //   return;
    // }

    // 검증 통과 시 에러 초기화
    setValidationErrors({});

    if (isAdditionalBurial && !isParentReservedPlot) {
      const bw = parseFloat(formData.cost.burial_work_fee) || 0;
      if (bw <= 0) {
        showError('매장작업비를 입력해주세요.');
        return;
      }
    }

    if (isEditMode && formData.type === '해약' && !String(formData.cancellation_date || '').trim()) {
      showError('해약일자를 입력해주세요.');
      return;
    }

    if (isEditMode && formData.type === '이장' && !String(formData.relocation_date || '').trim()) {
      showError('이장일자를 입력해주세요.');
      return;
    }

    const orderedAcquaintances = getOrderedAcquaintances(formData.acquaintances);
    const normalizedAcquaintances = orderedAcquaintances.map((acquaintance) => {
      const originalRelationship = (acquaintance.relationship || '').trim();
      const relationshipOther = (acquaintance.relationship_other || '').trim();

      return {
        ...acquaintance,
        relationship: getAcquaintanceRelationshipDisplay(acquaintance),
        relationship_other: originalRelationship === '기타' ? relationshipOther : ''
      };
    });

    const submitData = {
      ...formData,
      acquaintances: normalizedAcquaintances,
      documents: serializeDocumentsForApi(formData.documents),
    };
    // 총액은 서버 Cost.beforeSave에서 수수료 항목만 합산. 폼 calculateTotalCost()는 각자/추가비 등 포함해 DB와 불일치 → 전송 시 제외
    if (formData.cost != null) {
      const { total_cost: _omitTotalCost, ...costWithoutClientTotal } = formData.cost;
      submitData.cost = costWithoutClientTotal;
    }

    if (!isEditMode || formData.type !== '해약') {
      delete submitData.cancellation_date;
    }
    if (!isEditMode || formData.type !== '이장') {
      delete submitData.relocation_date;
    }

    contractMutation.mutate(submitData);
  };

  if (isLoadingContract) {
    return <div className="loading">계약 정보를 불러오는 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/contracts')}
            className="btn btn-outline"
          >
            <ArrowLeft size={16} className="mr-2" />
            목록으로
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAdditionalBurial && isParentReservedPlot
                ? '예정지 계약 → 정식 계약 전환'
                : isAdditionalBurial
                  ? '안치자 추가'
                  : isEditMode
                    ? '계약 수정'
                    : '신규 계약 등록'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdditionalBurial && isParentReservedPlot
                ? '예정지 계약을 정식 계약으로 전환하고 사망자 정보를 추가합니다.'
                : isAdditionalBurial
                  ? '기존 계약에 새로운 사망자를 추가합니다.'
                  : isEditMode
                    ? '계약 정보를 수정합니다.'
                    : '새로운 계약을 등록합니다.'}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {/* <button
            onClick={handleSendEForm}
            disabled={!canEdit || eFormMutation.isLoading}
            className="btn btn-outline"
          >
            <Send size={16} className="mr-2" />
            {eFormMutation.isLoading ? '발송 중...' : '계약서 발송'}
          </button> */}
          <button
            onClick={handleSubmit}
            disabled={!canEdit || contractMutation.isLoading}
            className="btn btn-primary"
          >
            <Save size={16} className="mr-2" />
            {contractMutation.isLoading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            // 안치자 추가 계약 모드일 때는 일부 탭만 표시
            ...(isAdditionalBurial ? [
              // '예정지' 계약인 경우 기본정보, 묘소정보 탭 추가
              ...(isParentReservedPlot ? [
                { id: 'basic', name: '기본정보', icon: FileText },
                { id: 'mausoleum', name: '묘소정보', icon: MapPin }
              ] : []),
              { id: 'deceased', name: '사망자정보', icon: User },
              { id: 'acquaintance', name: '연고자정보', icon: UserCheck },
              { id: 'documents', name: '서류정보', icon: File },
              { id: 'stone_objects', name: '석물정보', icon: Building },
              { id: 'engraving', name: '각자정보', icon: FileText },
              { id: 'cost', name: '비용정보', icon: DollarSign },
              { id: 'electronic', name: '전자계약서', icon: FileText }
            ] : [
              { id: 'basic', name: '기본정보', icon: FileText },
              { id: 'mausoleum', name: '묘소정보', icon: MapPin },
              { id: 'deceased', name: '사망자정보', icon: User },
              { id: 'acquaintance', name: '연고자정보', icon: UserCheck },
              { id: 'documents', name: '서류정보', icon: File },
              { id: 'stone_objects', name: '석물정보', icon: Building },
              { id: 'engraving', name: '각자정보', icon: FileText },
              ...(isEditMode ? [{ id: 'maintenance', name: '보수정보', icon: Building }] : []),
              { id: 'cost', name: '비용정보', icon: DollarSign },
              { id: 'electronic', name: '전자계약서', icon: FileText }
            ])
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {/* <Icon size={16} /> */}
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="space-y-6">
        {/* 기본정보 탭 - 일반 생성/수정 또는 예정지 안치자 추가 */}
        {activeTab === 'basic' && (!isAdditionalBurial || isParentReservedPlot) && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">기본 정보</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">{getFieldLabel('contract_date', FL.contract_date)} <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.contract_date}
                    onChange={(e) => handleInputChange('main', 'contract_date', e.target.value)}
                    className={`input ${getNestedFieldError(validationErrors, 'contract_date') ? 'border-red-500' : ''}`}
                    required
                  />
                  {getNestedFieldError(validationErrors, 'contract_date') && (
                    <p className="text-red-500 text-sm mt-1">{getNestedFieldError(validationErrors, 'contract_date')}</p>
                  )}
                </div>
                <div>
                  <label className="form-label">{getFieldLabel('type', FL.type)} <span className="text-red-500">*</span></label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('main', 'type', e.target.value)}
                    className="select"
                  >
                    {isEditMode ? (
                      <>
                        <option value="신규">신규</option>
                        <option value="이장">이장</option>
                        <option value="해약">해약</option>
                      </>
                    ) : (
                      <>
                        <option value="신규">신규</option>
                        <option value="예정지">예정지</option>
                      </>
                    )}
                  </select>
                </div>
                {isEditMode && formData.type === '해약' && (
                  <div>
                    <label className="form-label">{getFieldLabel('cancellation_date', FL.cancellation_date)} <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={formData.cancellation_date || ''}
                      onChange={(e) => handleInputChange('main', 'cancellation_date', e.target.value)}
                      className="input"
                      required
                    />
                  </div>
                )}
                {isEditMode && formData.type === '이장' && (
                  <div>
                    <label className="form-label">{getFieldLabel('relocation_date', FL.relocation_date)} <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={formData.relocation_date || ''}
                      onChange={(e) => handleInputChange('main', 'relocation_date', e.target.value)}
                      className="input"
                      required
                    />
                  </div>
                )}
                {isFieldVisible('memo') && (
                  <div className="md:col-span-2">
                    <label className="form-label">{getFieldLabel('memo', FL.memo)}</label>
                    <textarea
                      value={formData.memo}
                      onChange={(e) => handleInputChange('main', 'memo', e.target.value)}
                      className="input"
                      rows="3"
                      placeholder={`${getFieldLabel('memo', FL.memo)}를 입력하세요`}
                    />
                  </div>
                )}

                {/* 커스텀 필드 */}
                {isFieldEnabled('custom_field_1') && (
                  <div>
                    <label className="form-label">{getFieldLabel('custom_field_1', FL.custom_field_1)}</label>
                    <input
                      type="text"
                      value={formData.custom_field_1 || ''}
                      onChange={(e) => handleInputChange('main', 'custom_field_1', e.target.value)}
                      className="input"
                      placeholder={getFieldLabel('custom_field_1', FL.custom_field_1)}
                    />
                  </div>
                )}

                {isFieldEnabled('custom_field_2') && (
                  <div>
                    <label className="form-label">{getFieldLabel('custom_field_2', FL.custom_field_2)}</label>
                    <input
                      type="text"
                      value={formData.custom_field_2 || ''}
                      onChange={(e) => handleInputChange('main', 'custom_field_2', e.target.value)}
                      className="input"
                      placeholder={getFieldLabel('custom_field_2', FL.custom_field_2)}
                    />
                  </div>
                )}

                {isFieldEnabled('custom_field_3') && (
                  <div>
                    <label className="form-label">{getFieldLabel('custom_field_3', FL.custom_field_3)}</label>
                    <input
                      type="text"
                      value={formData.custom_field_3 || ''}
                      onChange={(e) => handleInputChange('main', 'custom_field_3', e.target.value)}
                      className="input"
                      placeholder={getFieldLabel('custom_field_3', FL.custom_field_3)}
                    />
                  </div>
                )}

                {isFieldEnabled('custom_field_4') && (
                  <div>
                    <label className="form-label">{getFieldLabel('custom_field_4', FL.custom_field_4)}</label>
                    <input
                      type="text"
                      value={formData.custom_field_4 || ''}
                      onChange={(e) => handleInputChange('main', 'custom_field_4', e.target.value)}
                      className="input"
                      placeholder={getFieldLabel('custom_field_4', FL.custom_field_4)}
                    />
                  </div>
                )}

                {isFieldEnabled('custom_field_5') && (
                  <div className="md:col-span-2">
                    <label className="form-label">{getFieldLabel('custom_field_5', FL.custom_field_5)}</label>
                    <textarea
                      value={formData.custom_field_5 || ''}
                      onChange={(e) => handleInputChange('main', 'custom_field_5', e.target.value)}
                      className="input"
                      rows="3"
                      placeholder={getFieldLabel('custom_field_5', FL.custom_field_5)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 묘소 정보 탭 - 일반 생성/수정 또는 예정지 안치자 추가 */}
        {activeTab === 'mausoleum' && (!isAdditionalBurial || isParentReservedPlot) && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">묘소 정보</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isFieldVisible('member_number') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('member_number', FL.member_number)}
                      {isFieldRequired('member_number') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.mausoleum.member_number}
                      onChange={(e) => handleInputChange('mausoleum', 'member_number', e.target.value)}
                      className="input"
                      placeholder={`${getFieldLabel('member_number', FL.member_number)}를 입력하세요`}
                    />
                  </div>
                )}

                {isFieldVisible('complex') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('complex', FL.complex)}
                      {isFieldRequired('complex') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.mausoleum.complex}
                      onChange={(e) => handleInputChange('mausoleum', 'complex', e.target.value)}
                      className={`input ${getNestedFieldError(validationErrors, 'mausoleum.complex') ? 'border-red-500' : ''}`}
                      placeholder={`${getFieldLabel('complex', FL.complex)}를 입력하세요`}
                    />
                    {getNestedFieldError(validationErrors, 'mausoleum.complex') && (
                      <p className="text-red-500 text-sm mt-1">{getNestedFieldError(validationErrors, 'mausoleum.complex')}</p>
                    )}
                  </div>
                )}

                {isFieldVisible('area') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('area', FL.area)}
                      {isFieldRequired('area') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.mausoleum.area}
                      onChange={(e) => handleInputChange('mausoleum', 'area', e.target.value)}
                      className="input"
                      placeholder={`${getFieldLabel('area', FL.area)}을 입력하세요`}
                    />
                  </div>
                )}

                {isFieldVisible('row_number') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('row_number', FL.row_number)}
                      {isFieldRequired('row_number') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.mausoleum.row_number}
                      onChange={(e) => handleInputChange('mausoleum', 'row_number', e.target.value)}
                      className="input"
                      placeholder={`${getFieldLabel('row_number', FL.row_number)}을 입력하세요`}
                    />
                  </div>
                )}

                {isFieldVisible('number') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('number', FL.number)}
                      {isFieldRequired('number') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.mausoleum.number}
                      onChange={(e) => handleInputChange('mausoleum', 'number', e.target.value)}
                      className="input"
                      placeholder={`${getFieldLabel('number', FL.number)}를 입력하세요`}
                    />
                  </div>
                )}

                {isFieldVisible('area_size') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('area_size', FL.area_size)}
                      {isFieldRequired('area_size') && <span className="text-red-500">*</span>}
                    </label>
                    <NumberInput
                      value={formData.mausoleum.area_size}
                      onChange={(size) => {
                        handleInputChange('mausoleum', 'area_size', size);
                        calculateFeesFromSize(size);
                      }}
                      allowDecimal={true}
                      className="input"
                      placeholder={`${getFieldLabel('area_size', FL.area_size)}를 입력하세요`}
                      min={0}
                      step={0.1}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      크기를 입력하면 사용료가 자동으로 계산됩니다. 관리비는 계약 기간 이후에 후불로 발생됩니다.
                    </p>
                  </div>
                )}

                {isFieldVisible('period') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('period', FL.period)}
                      {isFieldRequired('period') && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      value={formData.mausoleum.period}
                      onChange={(e) => handleInputChange('mausoleum', 'period', parseInt(e.target.value) || 0)}
                      className="input"
                      placeholder={`${getFieldLabel('period', FL.period)}을 입력하세요`}
                      min="0"
                    />
                  </div>
                )}

                {isFieldVisible('tomb_type') && (
                  <div>
                    <label className="form-label">
                      {getFieldLabel('tomb_type', FL.tomb_type)}
                      {isFieldRequired('tomb_type') && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={formData.mausoleum.tomb_type}
                      onChange={(e) => {
                        const newTombType = e.target.value;
                        handleInputChange('mausoleum', 'tomb_type', newTombType);

                        // 묘지형태 변경 시 number_of_tombs 초기화
                        // 매장묘로 변경: 빈 값으로 초기화 (Select Box 사용)
                        // 다른 형태로 변경: 1로 초기화 (NumberInput 사용)
                        if (newTombType === '매장묘') {
                          handleInputChange('mausoleum', 'number_of_tombs', '');
                        } else if (formData.mausoleum.tomb_type === '매장묘') {
                          // 기존이 매장묘였고 다른 형태로 변경되는 경우에만 초기화
                          handleInputChange('mausoleum', 'number_of_tombs', '1');
                        }
                      }}
                      className="select left-3"
                    >
                      <option value="">선택</option>
                      <option value="평장묘">평장묘</option>
                      <option value="가족평장묘">가족평장묘</option>
                      <option value="매장묘">매장묘</option>
                      <option value="봉안묘">봉안묘</option>
                      {/* <option value="납골묘">납골묘</option> */}
                      <option value="예정지">예정지</option>
                    </select>
                  </div>
                )}

                {isFieldVisible('number_of_tombs') && (
                  <div>
                    <label className="form-label">
                      {formData.mausoleum.tomb_type === '매장묘'
                        ? getFieldLabel('tomb_shape', FL.tomb_shape)
                        : getFieldLabel('number_of_tombs', FL.number_of_tombs)
                      }
                      {isFieldRequired('number_of_tombs') && <span className="text-red-500">*</span>}
                    </label>

                    {/* 매장묘일 때는 Select Box, 그 외에는 NumberInput */}
                    {formData.mausoleum.tomb_type === '매장묘' ? (
                      <select
                        value={formData.mausoleum.number_of_tombs || ''}
                        onChange={(e) => handleInputChange('mausoleum', 'number_of_tombs', e.target.value)}
                        className="select"
                      >
                        <option value="">선택</option>
                        <option value="합장">합장</option>
                        <option value="단분">단분</option>
                        <option value="쌍분">쌍분</option>
                      </select>
                    ) : (
                      <NumberInput
                        value={formData.mausoleum.number_of_tombs}
                        onChange={(num) => handleInputChange('mausoleum', 'number_of_tombs', num)}
                        className="input"
                        placeholder={`${getFieldLabel('number_of_tombs', FL.number_of_tombs)}를 입력하세요`}
                        min="1"
                        step="1"
                      />
                    )}
                  </div>
                )}

                {isFieldVisible('mausoleum_etc') && (
                  <div className="md:col-span-2">
                    <label className="form-label">
                      {getFieldLabel('mausoleum_etc', FL.mausoleum_etc)}
                    </label>
                    <textarea
                      value={formData.mausoleum.etc}
                      onChange={(e) => handleInputChange('mausoleum', 'etc', e.target.value)}
                      className="input"
                      rows="2"
                      placeholder={`${getFieldLabel('mausoleum_etc', FL.mausoleum_etc)} 사항을 입력하세요`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 사망자정보 탭 */}
        {activeTab === 'deceased' && (
          <div className="space-y-4">
            {/* '예정지' 계약인 경우 사망자 정보 선택사항 안내 */}
            {formData.type === '예정지' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  💡 <strong>'예정지' 계약</strong>은 땅만 미리 계약하는 것이므로 사망자 정보는 선택사항입니다.
                </p>
              </div>
            )}
            {formData.dead.map((dead, index) => (
              <div key={index} className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h3 className="text-lg font-semibold">
                      사망자 {index + 1}
                      {formData.type === '예정지' && <span className="text-gray-500 text-sm ml-2">(선택사항)</span>}
                    </h3>
                    {formData.dead.length > 1 && (
                      <button
                        onClick={() => removeDead(index)}
                        className="btn btn-danger"
                      >
                        <X size={16} />
                        삭제
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('dead_name', FL.dead_name)} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={dead.name}
                        onChange={(e) => handleInputChange('dead', 'name', e.target.value, index)}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('gender', FL.gender)} <span className="text-red-500">*</span></label>
                      <select
                        value={dead.gender}
                        onChange={(e) => handleInputChange('dead', 'gender', e.target.value, index)}
                        className="select"
                      >
                        <option value="">선택</option>
                        <option value="남">남</option>
                        <option value="여">여</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('dead_resident_number', FL.dead_resident_number)}</label>
                      <input
                        type="text"
                        value={dead.resident_number}
                        onChange={(e) => handleInputChange('dead', 'resident_number', e.target.value, index)}
                        className={`input ${validationErrors[`dead.${index}.resident_number`] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="000000-0000000"
                      />
                      {validationErrors[`dead.${index}.resident_number`] && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors[`dead.${index}.resident_number`]}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('burial_method_1', FL.burial_method_1)} <span className="text-red-500">*</span></label>
                      <select
                        value={dead.burial_method_1}
                        onChange={(e) => handleInputChange('dead', 'burial_method_1', e.target.value, index)}
                        className="select"
                      >
                        <option value="">선택</option>
                        {BURIAL_METHODS_1.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('burial_method_2', FL.burial_method_2)}</label>
                      <select
                        value={dead.burial_method_2}
                        onChange={(e) => handleInputChange('dead', 'burial_method_2', e.target.value, index)}
                        className="select"
                      >
                        <option value="">선택</option>
                        {BURIAL_METHODS_2.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </div>
                    {/* 묘지형태는 묘소 정보 탭으로 이동 */}
                    <div>
                      <label className="form-label">{getFieldLabel('death_date', FL.death_date)}</label>
                      <input
                        type="date"
                        value={dead.death_date}
                        onChange={(e) => handleInputChange('dead', 'death_date', e.target.value, index)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('death_place', FL.death_place)}</label>
                      <input
                        type="text"
                        value={dead.death_place || ''}
                        onChange={(e) => handleInputChange('dead', 'death_place', e.target.value, index)}
                        className="input"
                        placeholder="사망장소를 입력하세요"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('death_cause', FL.death_cause)}</label>
                      <input
                        type="text"
                        value={dead.death_cause || ''}
                        onChange={(e) => handleInputChange('dead', 'death_cause', e.target.value, index)}
                        className="input"
                        placeholder="사망원인을 입력하세요"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('burial_date', FL.burial_date)}</label>
                      <input
                        type="date"
                        value={dead.report_date}
                        onChange={(e) => handleInputChange('dead', 'report_date', e.target.value, index)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('dead_address', FL.dead_address)}</label>
                      {/* <AddressSearch
                        onAddressSelect={(address) => handleAddressSelect(address, index, 'dead')}
                        placeholder="주소를 검색하세요"
                      /> */}
                      <AddressSearch
                        onAddressSelect={(address) => handleAddressSelect(address, index, 'dead')}
                        placeholder={`${getFieldLabel('dead_address', FL.dead_address)}를 검색하세요`}
                        defaultValue={dead.road_address || dead.address || ''} // 추가
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">{getFieldLabel('dead_address_detail', FL.dead_address_detail)}</label>
                      <input
                        type="text"
                        value={dead.address_detail}
                        onChange={(e) => handleInputChange('dead', 'address_detail', e.target.value, index)}
                        className="input"
                        placeholder={`${getFieldLabel('dead_address_detail', FL.dead_address_detail)}를 입력하세요`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addDead}
              className="btn btn-outline w-full"
            >
              <Plus size={16} className="mr-2" />
              사망자 추가
            </button>
          </div>
        )}

        {/* 연고자정보 탭 */}
        {activeTab === 'acquaintance' && (
          <div className="space-y-4">
            {formData.acquaintances.map((acquaintance, index) => (
              <div key={index} className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h3 className="text-lg font-semibold">연고자 {index + 1}</h3>
                    {formData.acquaintances.length > 1 && (
                      <button
                        onClick={() => removeAcquaintance(index)}
                        className="btn btn-danger"
                      >
                        <X size={16} />
                        삭제
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('acquaintance_name', FL.acquaintance_name)} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={acquaintance.name}
                        onChange={(e) => handleInputChange('acquaintance', 'name', e.target.value, index)}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('relationship', FL.relationship)} <span className="text-red-500">*</span></label>
                      <div className="flex space-x-2">
                        <select
                          value={acquaintance.relationship}
                          onChange={(e) => handleInputChange('acquaintance', 'relationship', e.target.value, index)}
                          className="select flex-1"
                        >
                          <option value="">선택</option>
                          {RELATIONSHIPS.map(rel => (
                            <option key={rel} value={rel}>{rel}</option>
                          ))}
                        </select>
                        {acquaintance.relationship === '기타' && (
                          <input
                            type="text"
                            value={acquaintance.relationship_other}
                            onChange={(e) => handleInputChange('acquaintance', 'relationship_other', e.target.value, index)}
                            className="input flex-1"
                            placeholder="관계 입력"
                          />
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('phone', FL.phone)} <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        value={acquaintance.phone}
                        onChange={(e) => handleInputChange('acquaintance', 'phone', e.target.value, index)}
                        className={`input ${validationErrors[`acquaintances.${index}.phone`] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="010-1234-5678"
                      />
                      {validationErrors[`acquaintances.${index}.phone`] && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors[`acquaintances.${index}.phone`]}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('acquaintance_resident_number', FL.acquaintance_resident_number)} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={acquaintance.resident_number}
                        onChange={(e) => handleInputChange('acquaintance', 'resident_number', e.target.value, index)}
                        className={`input ${validationErrors[`acquaintances.${index}.resident_number`] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="000000-0000000"
                      />
                      {validationErrors[`acquaintances.${index}.resident_number`] && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors[`acquaintances.${index}.resident_number`]}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('acquaintance_address', FL.acquaintance_address)}<span className="text-red-500">*</span></label>
                      {/* <AddressSearch
                        onAddressSelect={(address) => handleAddressSelect(address, index)}
                        placeholder="주소를 검색하세요"
                      /> */}
                      <AddressSearch
                        onAddressSelect={(address) => handleAddressSelect(address, index)}
                        placeholder={`${getFieldLabel('acquaintance_address', FL.acquaintance_address)}를 검색하세요`}
                        defaultValue={acquaintance.road_address || acquaintance.address || ''} // 추가
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">{getFieldLabel('acquaintance_address_detail', FL.acquaintance_address_detail)}</label>
                      <input
                        type="text"
                        value={acquaintance.detail_address}
                        onChange={(e) => handleInputChange('acquaintance', 'detail_address', e.target.value, index)}
                        className="input"
                        placeholder={`${getFieldLabel('acquaintance_address_detail', FL.acquaintance_address_detail)}를 입력하세요`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={acquaintance.is_primary}
                          onChange={(e) => handleInputChange('acquaintance', 'is_primary', e.target.checked, index)}
                          className="mr-2"
                        />
                        {getFieldLabel('is_primary_contact', FL.is_primary_contact)}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addAcquaintance}
              className="btn btn-outline w-full"
            >
              <Plus size={16} className="mr-2" />
              연고자 추가
            </button>
          </div>
        )}

        {/* 비용정보 탭 */}
        {activeTab === 'cost' && (
          <div className="space-y-6">
            {/* 기본 비용 */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">기본 비용</h3>
              </div>
              <div className="card-body">
                {isAdditionalBurial ? (
                  // 안치자 추가: 사용료·매장작업비·설치비 (저장 시 상위 계약 비용상세에 회차별 항목으로 추가)
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('usage_fee', FL.usage_fee)} (이번 안치자 추가)</label>
                      <NumberInput
                        value={formData.cost.usage_fee}
                        onChange={(val) => handleInputChange('cost', 'usage_fee', val)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('burial_work_fee', FL.burial_work_fee)} (이번 안치자 추가) <span className="text-red-500">*</span></label>
                      <NumberInput
                        value={formData.cost.burial_work_fee}
                        onChange={(val) => handleInputChange('cost', 'burial_work_fee', val)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('installation_fee', FL.installation_fee)} (이번 안치자 추가)</label>
                      <NumberInput
                        value={formData.cost.installation_fee}
                        onChange={(val) => handleInputChange('cost', 'installation_fee', val)}
                        className="input"
                      />
                    </div>
                    <p className="md:col-span-2 text-sm text-gray-500">
                      상위 계약 비용 목록에는 최초 계약을 제외한 회차 번호가 붙습니다. (예: 사용료 2, 매장작업비 2…)
                    </p>
                  </div>
                ) : (
                  // 일반 계약 모드: 모든 비용 입력
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('usage_fee', FL.usage_fee)}</label>
                      <NumberInput
                        value={formData.cost.usage_fee}
                        onChange={() => { }}
                        className="input bg-gray-50"
                        readOnly={true}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        계약 평수와 관리자 설정에 따라 자동 계산됩니다.
                      </p>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('management_fee', FL.management_fee)}</label>
                      <NumberInput
                        value={getCalculatedManagementFee()}
                        onChange={() => { }}
                        className="input bg-gray-50"
                        readOnly={true}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        관리비 = 기본관리비 × 평수 × 계약기간
                      </p>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('burial_work_fee', FL.burial_work_fee)}</label>
                      <NumberInput
                        value={formData.cost.burial_work_fee}
                        onChange={(val) => handleInputChange('cost', 'burial_work_fee', val)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('installation_fee', FL.installation_fee)}</label>
                      <NumberInput
                        value={formData.cost.installation_fee}
                        onChange={(val) => handleInputChange('cost', 'installation_fee', val)}
                        className="input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 석물 비용 */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">석물 비용</h3>
              </div>
              <div className="card-body">
                <div className="text-sm text-gray-600 mb-4">
                  석물 정보 탭에서 등록한 석물의 총 비용이 자동으로 계산됩니다.
                </div>
                <div className="text-lg font-semibold text-blue-600">
                  석물 총 비용: {formatCurrency(stoneCost)}원
                </div>
              </div>
            </div>

            {/* 각자 비용 */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">각자 비용</h3>
              </div>
              <div className="card-body">
                <div className="text-sm text-gray-600 mb-4">
                  각자 정보 탭에서 등록한 각자 비용이 자동으로 계산됩니다.
                </div>
                <div className="text-lg font-semibold text-green-600">
                  각자 총 비용: {formatCurrency(formData.cost.engraving_fee || 0)}원
                </div>
              </div>
            </div>

            {/* 보수 비용 (수정 모드에서만) */}
            {isEditMode && (
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">보수 비용</h3>
                </div>
                <div className="card-body">
                  <div className="text-sm text-gray-600 mb-4">
                    보수 정보 탭에서 등록한 보수 비용이 자동으로 계산됩니다.
                  </div>
                  <div className="text-lg font-semibold text-orange-600">
                    보수 총 비용: {formatCurrency(formData.cost.maintenance_fee || 0)}원
                  </div>
                </div>
              </div>
            )}

            {/* 추가 비용 */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">추가 비용</h3>
              </div>
              <div className="card-body">
                {(formData.cost.additional_costs || []).map((cost, index) => (
                  <div key={index} className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={cost.name}
                      onChange={(e) => {
                        const newCosts = [...(formData.cost.additional_costs || [])];
                        newCosts[index].name = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          cost: { ...prev.cost, additional_costs: newCosts }
                        }));
                      }}
                      className="input w-full md:flex-[7]"
                      placeholder="비용 항목명"
                    />
                    <div className="w-full md:flex-[3]">
                      <NumberInput
                        value={cost.amount}
                        onChange={(val) => {
                          const newCosts = [...(formData.cost.additional_costs || [])];
                          newCosts[index].amount = val;
                          setFormData(prev => ({
                            ...prev,
                            cost: { ...prev.cost, additional_costs: newCosts }
                          }));
                        }}
                        className="input w-full"
                        placeholder="금액"
                      />
                    </div>
                    <button
                      onClick={() => removeCostItem(index)}
                      className="btn btn-danger shrink-0"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addCostItem}
                  className="btn btn-outline"
                >
                  <Plus size={16} className="mr-2" />
                  비용 항목 추가
                </button>
              </div>
            </div>

            {/* 총 비용 요약 */}
            <div className="card bg-blue-50">
              <div className="card-body">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-blue-900">
                    총 비용: {formatCurrency(calculateTotalCost())}원
                  </h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 전자계약서 탭 */}
        {activeTab === 'electronic' && (
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h2 className="text-lg font-semibold">전자 계약서 미리보기</h2>
              <button
                onClick={() => setShowElectronicModal(true)}
                className="btn btn-outline btn-sm"
              >
                다른 템플릿 선택
              </button>
            </div>
            <div className="card-body p-0">
              <div style={{ width: '100%', height: '800px', backgroundColor: '#f5f5f5' }}>
                <iframe
                  id="eformsign_iframe"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="전자 계약서 미리보기"
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* 서류정보 탭 */}
        {activeTab === 'documents' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">서류 정보</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([key, label]) => (
                  <div key={key} className="space-y-2">
                    <label className="form-label">{getFieldLabel(key, FL[key] || label)}</label>
                    <div className="space-y-3">
                      {getDocumentFilesForUi(formData.documents, key).map((file, idx) => (
                        <div key={file.id ?? `${key}-${idx}-${file.url || 'new'}`} className="flex gap-2 items-start">
                          <div className="flex-1 min-w-0">
                            <FileUpload
                              value={file.url || ''}
                              filename={file.name || ''}
                              onChange={(fileUrl, fileName) => {
                                setFormData((prev) => {
                                  const next = { ...prev.documents };
                                  const cur = [...normalizeDocumentSlotToArray(next[key])];
                                  cur[idx] = { ...cur[idx], url: fileUrl, name: fileName };
                                  next[key] = cur;
                                  return { ...prev, documents: next };
                                });
                              }}
                              onRemove={() => {
                                setFormData((prev) => {
                                  const next = { ...prev.documents };
                                  let cur = [...normalizeDocumentSlotToArray(next[key])];
                                  cur.splice(idx, 1);
                                  next[key] = cur;
                                  return { ...prev, documents: next };
                                });
                              }}
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm text-sm"
                      onClick={() => {
                        setFormData((prev) => {
                          const next = { ...prev.documents };
                          const cur = [...normalizeDocumentSlotToArray(next[key])];
                          cur.push({ url: '', name: '' });
                          next[key] = cur;
                          return { ...prev, documents: next };
                        });
                      }}
                    >
                      <Plus size={14} className="inline mr-1" />
                      파일 추가
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 석물정보 탭 */}
        {activeTab === 'stone_objects' && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">석물 정보</h2>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  {isEditMode ? '계약 수정 시 석물 정보를 관리할 수 있습니다.' : '계약 등록 시 석물 정보를 미리 입력할 수 있습니다. 계약 저장 후 석물 정보가 자동으로 등록됩니다.'}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(formData.stone_objects || []).map((stone, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium">{stone.stone_name || '석물명 미지정'}</h4>
                        <button
                          onClick={() => {
                            const newStoneObjects = [...(formData.stone_objects || [])];
                            newStoneObjects.splice(index, 1);
                            setFormData(prev => ({ ...prev, stone_objects: newStoneObjects }));
                            // 석물 삭제 후 비용 재계산
                            setTimeout(() => calculateStoneCost(), 0);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="form-label text-sm">{getFieldLabel('stone_name', FL.stone_name)}</label>
                          <select
                            value={stone.stone_name}
                            onChange={(e) => {
                              const newStoneObjects = [...(formData.stone_objects || [])];
                              const selectedStoneType = e.target.value;
                              newStoneObjects[index].stone_name = selectedStoneType;

                              // 선택된 석물 종류의 비용을 자동으로 설정
                              if (selectedStoneType && stoneCosts) {
                                const selectedCost = stoneCosts.find(cost => cost.stone_type === selectedStoneType);
                                if (selectedCost) {
                                  newStoneObjects[index].cost = selectedCost.cost;
                                }
                              }

                              setFormData(prev => ({ ...prev, stone_objects: newStoneObjects }));
                              // 석물 종류 변경 후 비용 재계산
                              setTimeout(() => calculateStoneCost(), 0);
                            }}
                            className="input text-sm"
                          >
                            <option value="">선택하세요</option>
                            {stoneCosts && stoneCosts.length > 0 ? (
                              stoneCosts.map((cost) => (
                                <option key={cost.id} value={cost.stone_type}>
                                  {cost.stone_type} - {formatCurrency(cost.cost)}
                                </option>
                              ))
                            ) : (
                              <>
                                {/* <option value="묘테">묘테</option>
                                  <option value="상석">상석</option>
                                  <option value="화병">화병</option>
                                  <option value="향로">향로</option>
                                  <option value="기타">기타</option> */}
                              </>
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="form-label text-sm">{getFieldLabel('stone_cost', FL.stone_cost)}</label>
                          <NumberInput
                            value={stone.cost}
                            onChange={(val) => {
                              const newStoneObjects = [...(formData.stone_objects || [])];
                              newStoneObjects[index].cost = val;
                              setFormData(prev => ({ ...prev, stone_objects: newStoneObjects }));
                              setTimeout(() => calculateStoneCost(), 0);
                            }}
                            className="input text-sm"
                            placeholder="비용을 입력하세요"
                          />
                        </div>
                        {isFieldVisible('stone_memo') && (
                        <div>
                          <label className="form-label text-sm">{getFieldLabel('stone_memo', FL.stone_memo)}</label>
                          <textarea
                            value={stone.memo}
                            onChange={(e) => {
                              const newStoneObjects = [...(formData.stone_objects || [])];
                              newStoneObjects[index].memo = e.target.value;
                              setFormData(prev => ({ ...prev, stone_objects: newStoneObjects }));
                            }}
                            className="input text-sm"
                            rows="2"
                            placeholder={`${getFieldLabel('stone_memo', FL.stone_memo)}를 입력하세요`}
                          />
                        </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newStone = {
                        stone_name: '',
                        cost: 0,
                        fee_status: '미납',
                        payment_method: '',
                        payment_date: '',
                        memo: ''
                      };
                      setFormData(prev => ({
                        ...prev,
                        stone_objects: [...(prev.stone_objects || []), newStone]
                      }));
                      // 석물 추가 후 비용 재계산
                      setTimeout(() => calculateStoneCost(), 0);
                    }}
                    className="btn btn-outline w-full"
                  >
                    <Plus size={16} className="mr-2" />
                    석물 추가
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 각자정보 탭 */}
        {activeTab === 'engraving' && (
          <div className="space-y-4">
            {(formData.engraving || []).map((engraving, index) => (
              <div key={index} className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h3 className="text-lg font-semibold">각자 정보 {index + 1}</h3>
                    <button
                      onClick={() => removeEngraving(index)}
                      className="btn btn-danger"
                    >
                      <X size={16} />
                      삭제
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('engraving_type', FL.engraving_type)}</label>
                      <select
                        value={engraving.type}
                        onChange={(e) => {
                          const newEngraving = [...(formData.engraving || [])];
                          newEngraving[index].type = e.target.value;
                          setFormData(prev => ({ ...prev, engraving: newEngraving }));
                        }}
                        className="select"
                      >
                        {ENGRAVING_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('engraving_fee', FL.engraving_fee)}</label>
                      <NumberInput
                        value={engraving.amount}
                        onChange={(val) => {
                          const newEngraving = [...(formData.engraving || [])];
                          newEngraving[index].amount = val;
                          setFormData(prev => ({ ...prev, engraving: newEngraving }));
                          setTimeout(() => calculateEngravingCost(), 0);
                        }}
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">{getFieldLabel('content_file', FL.content_file)}</label>
                      <div className="space-y-2">
                        {/* 기존 텍스트 내용 (읽기 전용) */}
                        {engraving.content && !engraving.content_file && (
                          <div className="p-3 bg-gray-50 rounded border">
                            <p className="text-sm text-gray-600 mb-2">기존 내용:</p>
                            <p className="text-sm">{engraving.content}</p>
                          </div>
                        )}

                        {/* 파일 업로드 영역 */}
                        {engraving.content_file ? (
                          // 업로드된 파일 표시
                          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                            <div className="flex items-center gap-2">
                              <CheckCircle size={16} className="text-green-600" />
                              <File size={16} className="text-green-600" />
                              <div className="flex flex-col">
                                <span className="text-sm text-green-800 font-medium">
                                  {engraving.content_file_name || ''}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleEngravingFileRemove(index)}
                              className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 hover:bg-red-200 text-red-500 hover:text-red-700 transition-all duration-200 hover:scale-110"
                              title="파일 삭제"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          // 파일 업로드 영역
                          <div
                            className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                            onClick={() => document.getElementById(`engraving-file-${index}`).click()}
                          >
                            <input
                              id={`engraving-file-${index}`}
                              type="file"
                              accept=".txt,.doc,.docx,.pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleEngravingFileUpload(e.target.files[0], index)}
                              className="hidden"
                              style={{ display: 'none' }}
                            />
                            <div className="flex items-center justify-center gap-2">
                              <Upload size={20} className="text-gray-400" />
                              <span className="text-sm text-gray-600">파일 업로드</span>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500">
                          지원 형식: TXT, DOC, DOCX, PDF, JPG, JPEG, PNG
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addEngraving}
              className="btn btn-outline w-full"
            >
              <Plus size={16} className="mr-2" />
              각자 정보 추가
            </button>
          </div>
        )}

        {/* 보수정보 탭 (수정 모드에서만) */}
        {activeTab === 'maintenance' && isEditMode && (
          <div className="space-y-4">
            {(formData.maintenance || []).map((maintenance, index) => (
              <div key={index} className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h3 className="text-lg font-semibold">보수 정보 {index + 1}</h3>
                    <button
                      onClick={() => removeMaintenance(index)}
                      className="btn btn-danger"
                    >
                      <X size={16} />
                      삭제
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">{getFieldLabel('maintenance_content', FL.maintenance_content)}</label>
                      <input
                        type="text"
                        value={maintenance.content}
                        onChange={(e) => {
                          const newMaintenance = [...formData.maintenance];
                          newMaintenance[index].content = e.target.value;
                          setFormData(prev => ({ ...prev, maintenance: newMaintenance }));
                        }}
                        className="input"
                        placeholder="보수 내용을 입력하세요"
                      />
                    </div>
                    <div>
                      <label className="form-label">{getFieldLabel('maintenance_cost', FL.maintenance_cost)}</label>
                      <NumberInput
                        value={maintenance.amount}
                        onChange={(val) => {
                          const newMaintenance = [...formData.maintenance];
                          newMaintenance[index].amount = val;
                          setFormData(prev => ({ ...prev, maintenance: newMaintenance }));
                          setTimeout(() => calculateMaintenanceCost(), 0);
                        }}
                        className="input"
                        placeholder="보수 비용을 입력하세요"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addMaintenance}
              className="btn btn-outline w-full"
            >
              <Plus size={16} className="mr-2" />
              보수 정보 추가
            </button>
          </div>
        )}
      </div>

      {/* 템플릿 선택 모달 */}
      <TemplateSelectionModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={availableTemplates}
        onSelectTemplate={handleTemplateSelect}
        isLoading={false}
      />

      {/* 전자 계약서 미리보기 모달 */}
      <ElectronicContractModal
        isOpen={showElectronicModal}
        onClose={() => setShowElectronicModal(false)}
        isAdditionalBurial={isAdditionalBurial}
        templates={availableTemplates}
        onSelectTemplate={(template) => {
          console.log('Selected template for preview:', template);
        }}
        contractData={isAdditionalBurial && parentContract && !isParentReservedPlot ? (() => {
          // 안치자 추가 계약 모드: 기존 계약 정보 + 새 정보 조합
          const today = new Date();
          const formattedDate = `${today.getFullYear()}년 ${String(today.getMonth() + 1).padStart(2, '0')}월 ${String(today.getDate()).padStart(2, '0')}일`;

          // order 계산: 기존 childContracts의 최대 order + 1
          console.log('parentContract.childContracts:', parentContract.childContracts);
          const existingOrders = parentContract.childContracts
            ?.map(child => {
              console.log('child:', child, 'child.order:', child.order);
              return child.order;
            })
            .filter(order => order !== null && order !== undefined && !isNaN(order)) || [];
          console.log('existingOrders:', existingOrders);
          const maxOrder = existingOrders.length > 0 ? Math.max(...existingOrders) : 0;
          const order = maxOrder + 1;
          console.log('calculated order:', order);

          // 묘적번호 포맷팅
          const memberNumber = parentContract.mausoleum?.member_number
            ? `${parentContract.mausoleum.member_number}-${order}`
            : '';

          // 새로 입력된 석물 비용 계산
          const newStoneCost = formData.stone_objects?.reduce((sum, stone) => {
            return sum + (parseFloat(stone.cost) || 0);
          }, 0) || 0;

          // 새로 입력된 각자 비용 계산
          console.log('formData.engraving:', formData.engraving);
          const newEngravingCost = formData.engraving?.reduce((sum, eng) => {
            const amount = parseFloat(eng.amount) || 0;
            console.log('eng:', eng, 'amount:', amount);
            return sum + amount;
          }, 0) || 0;
          console.log('newEngravingCost:', newEngravingCost);
          console.log('formData.cost.burial_work_fee:', formData.cost.burial_work_fee);
          const addUsage = parseFloat(formData.cost.usage_fee) || 0;
          const addBurial = parseFloat(formData.cost.burial_work_fee) || 0;
          const addInstall = parseFloat(formData.cost.installation_fee) || 0;
          const calculatedTotalCost = addUsage + addBurial + addInstall + newEngravingCost + newStoneCost;
          console.log('calculatedTotalCost:', calculatedTotalCost);

          return {
            ...formData,
            // 기존 계약 정보 사용
            mausoleum: {
              ...parentContract.mausoleum,
              member_number: memberNumber // 포맷팅된 묘적번호
            },
              acquaintances: getOrderedAcquaintances(formData.acquaintances), // 주연고자 우선 순서 보장
            // 새 사망자 정보만 사용
            dead: formData.dead.filter(d => d.name), // 이름이 있는 사망자만
            // 날짜는 현재 날짜
            // contract_date: formattedDate,
            contract_date: today,
            // 비용 정보: 이번 안치자 추가분 (전자계약서·합계용)
            cost: {
              ...formData.cost,
              usage_fee: addUsage,
              management_fee: 0,
              installation_fee: addInstall,
              total_cost: calculatedTotalCost,
              burial_work_fee: addBurial,
              stone_processing_fee: newStoneCost,
              engraving_fee: newEngravingCost
            },
            // 석물과 각자는 새로 입력된 것만
            stone_objects: formData.stone_objects,
            engraving: formData.engraving
          };
        })() : formData}
      />
    </div>
  );
};

export default ContractFormV5; 